import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Grid,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Link,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Edit as EditIcon,
  Edit,
  CloudUpload,
  Delete,
  Save,
  CheckCircle,
  SwapHoriz,
  Add,
  Fullscreen,
  FullscreenExit,
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import api from '../../api/axios';
import { requestService, Request } from '../../services/requestService';
import { TO_DROP_DOWN_OPTIONS } from '../../constants/toDropDownOptions';
import WhatsAppService from '../../services/whatsapp.service';
import { systemSettingService } from '../../services/systemSettingService';
import { serviceProviderService } from '../../services/serviceProviderService';
import { insuranceClientService } from '../../services/insuranceClientService';
import { customerService } from '../../services/customerService';
import { servicePackageService } from '../../services/servicePackageService';
import { insuranceCompanyService } from '../../services/insuranceCompanyService';
import CreateInvoiceModal from '../../components/CreateInvoiceModal';
import AddPaymentModal from '../../components/AddPaymentModal';
import { NumberTextField } from '../../components/common';
import AutomationMap from '../../components/service-providers/AutomationMap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import InvoiceService from '../../services/invoice.service';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import { descriptionWithMapLink } from '../../utils/descriptionWithMapLink';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Message {
  id: number;
  phoneNumber: string;
  message: string;
  direction: 'inbound' | 'outbound';
  sentAt: string;
  messageType?: string;
  mediaUrl?: string;
}

const AutomationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [requests, setRequests] = useState<Request[]>([]);

  // Helper function to format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    // If it doesn't start with +, add it
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Helper function to open WhatsApp chat
  const openWhatsAppChat = (phone: string) => {
    const formattedPhone = formatPhoneForWhatsApp(phone);
    if (formattedPhone) {
      window.open(`https://wa.me/${formattedPhone.replace('+', '')}`, '_blank');
    }
  };
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTab, setChatTab] = useState<'customer' | 'provider'>('customer');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [autoAssign, setAutoAssign] = useState<boolean>(false);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [editLocationLatitude, setEditLocationLatitude] = useState<number | null>(null);
  const [editLocationLongitude, setEditLocationLongitude] = useState<number | null>(null);
  const [editMapCenter, setEditMapCenter] = useState<[number, number]>([23.5859, 58.4059]); // Muscat, Oman
  const [beforePhotoFile, setBeforePhotoFile] = useState<File | null>(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState<string | null>(null);
  const [afterPhotoFile, setAfterPhotoFile] = useState<File | null>(null);
  const [afterPhotoPreview, setAfterPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageErrors, setImageErrors] = useState<{ before: boolean; after: boolean }>({ before: false, after: false });
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [assigningRequest, setAssigningRequest] = useState<Request | null>(null);
  const [providerToAssign, setProviderToAssign] = useState<any>(null);
  const [changeProviderOpen, setChangeProviderOpen] = useState(false);
  const [changingRequest, setChangingRequest] = useState<Request | null>(null);
  const [newProvider, setNewProvider] = useState<any>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [requestForInvoice, setRequestForInvoice] = useState<Request | null>(null);
  const [requestForPayment, setRequestForPayment] = useState<Request | null>(null);
  const [invoicesModalOpen, setInvoicesModalOpen] = useState(false);
  const [providerInvoice, setProviderInvoice] = useState<any>(null);
  const [clientInvoice, setClientInvoice] = useState<any>(null);
  const [insuranceInvoice, setInsuranceInvoice] = useState<any>(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [customerInvoiceItems, setCustomerInvoiceItems] = useState<any[]>([]);
  const [insuranceInvoiceItems, setInsuranceInvoiceItems] = useState<any[]>([]);
  const [editInvoiceItemModalOpen, setEditInvoiceItemModalOpen] = useState(false);
  const [editingInvoiceItem, setEditingInvoiceItem] = useState<any>(null);
  const [editingInvoiceType, setEditingInvoiceType] = useState<'customer' | 'insurance' | null>(null);
  const [editItemFormData, setEditItemFormData] = useState({
    description: '',
    quantity: 1,
    unitPrice: 0,
    discount: 0,
    tax: 0,
    total: 0,
    apply5PercentTax: false
  });
  const [savingInvoiceItem, setSavingInvoiceItem] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [providerInvoiceItems, setProviderInvoiceItems] = useState<any[]>([]);
  const [clientInvoiceItems, setClientInvoiceItems] = useState<any[]>([]);
  const [insuranceInvoiceItemsAllModal, setInsuranceInvoiceItemsAllModal] = useState<any[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [insuranceClients, setInsuranceClients] = useState<any[]>([]);
  const [regularCustomers, setRegularCustomers] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientPackageInfo, setClientPackageInfo] = useState<any>(null);
  const [checkingClient, setCheckingClient] = useState(false);
  const [countryCode, setCountryCode] = useState('+968');
  const [locationLatitude, setLocationLatitude] = useState<number | null>(null);
  const [locationLongitude, setLocationLongitude] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([23.5859, 58.4059]); // Muscat, Oman
  const [createFormData, setCreateFormData] = useState({
    phoneNumber: '',
    serviceCode: '',
    serviceType: ''
  });
  // Assign client modal state
  const [assignClientModalOpen, setAssignClientModalOpen] = useState(false);
  const [assigningRequestForClient, setAssigningRequestForClient] = useState<Request | null>(null);
  const [selectedClientToAssign, setSelectedClientToAssign] = useState<any>(null);
  const [creatingNewClient, setCreatingNewClient] = useState(false);
  const [newClientFormData, setNewClientFormData] = useState({
    vinNumber: '',
    customerName: '',
    phoneNumber: '',
    email: '',
    plateNumber: '',
    carMake: '',
    carModel: '',
    carYear: '',
    policyNumber: '',
    policyStartDate: '',
    policyEndDate: '',
    servicePackage: '',
    insuranceCompany: '',
    notes: '',
    isActive: true
  });
  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
  const [assigningClient, setAssigningClient] = useState(false);
  const [kanbanFullscreen, setKanbanFullscreen] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Pagination states for each status column
  const [statusPages, setStatusPages] = useState<Record<string, number>>({
    pending: 1,
    assigned: 1,
    active: 1,
    invoice_sent: 1,
    payment_received: 1,
  });
  const ITEMS_PER_PAGE = 25;

  // Background refresh function that doesn't show loading spinner
  const refreshRequestsInBackground = async () => {
    try {
      setIsRefreshing(true);
      const response = await requestService.getAll({
        'populate[serviceProvider]': '*',
        'populate[insuranceClient]': '*',
        'populate[customer]': '*',
        'populate[invoice]': '*',
        'populate[invoices]': '*',
        'populate[evidencePhotoBefore]': '*',
        'populate[evidencePhotoAfter]': '*',
        sort: 'createdAt:desc',
        'pagination[pageSize]': 1000
      });
      
      const requestsData = response.data || [];
      setRequests(requestsData);
    } catch (err: any) {
      console.error('Error refreshing requests:', err);
      // Don't show error for background refreshes to avoid interrupting user
    } finally {
      setIsRefreshing(false);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    await loadRequests();
  };

  // Toggle auto-refresh
  const handleToggleAutoRefresh = (enabled: boolean) => {
    setAutoRefreshEnabled(enabled);
  };

  useEffect(() => {
    loadRequests();
    loadAutoAssignSetting();
    loadServiceProviders();
    loadAllCustomers();
    loadServicePackages();
    loadInsuranceCompanies();
  }, []);

  // Set up auto-refresh interval based on toggle state
  useEffect(() => {
    // Clear existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    // Set up new interval if auto-refresh is enabled
    if (autoRefreshEnabled) {
      autoRefreshIntervalRef.current = setInterval(() => {
        refreshRequestsInBackground();
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [autoRefreshEnabled]);

  const loadServicePackages = async () => {
    try {
      const response = await servicePackageService.getAll();
      const packagesData = Array.isArray(response) ? response : (response?.data || []);
      setServicePackages(packagesData);
    } catch (error) {
      console.error('Error loading service packages:', error);
      setServicePackages([]);
    }
  };

  const loadInsuranceCompanies = async () => {
    try {
      const response = await insuranceCompanyService.getAll();
      const companiesData = Array.isArray(response) ? response : (response?.data || []);
      setInsuranceCompanies(companiesData);
    } catch (error) {
      console.error('Error loading insurance companies:', error);
      setInsuranceCompanies([]);
    }
  };

  // Fix map size when map tab is selected
  useEffect(() => {
    if (activeTab === 1) {
      const invalidateMapSize = () => {
        try {
          if ((window as any).L) {
            const containers = document.querySelectorAll('.leaflet-container');
            containers.forEach((container: any) => {
              // Check if container has dimensions before trying to invalidate
              if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
                // Try to get map instance safely
                const mapInstance = container._leaflet_id ? (window as any).L.Map.prototype.getContainer?.() : null;
                if (mapInstance && typeof mapInstance.invalidateSize === 'function') {
                  mapInstance.invalidateSize();
                } else if (container._leaflet && typeof container._leaflet.invalidateSize === 'function') {
                  container._leaflet.invalidateSize();
                }
              }
            });
          }
        } catch (error) {
          // Silently handle errors - map might not be ready yet
          console.debug('Map size invalidation skipped:', error);
        }
      };
      
      // Multiple attempts with increasing delays to ensure map resizes properly
      const timers = [
        setTimeout(invalidateMapSize, 100),
        setTimeout(invalidateMapSize, 300),
        setTimeout(invalidateMapSize, 500),
        setTimeout(invalidateMapSize, 1000),
        setTimeout(invalidateMapSize, 2000)
      ];
      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [activeTab]);

  const loadServiceProviders = async () => {
    try {
      const response = await serviceProviderService.getAll({
        'pagination[pageSize]': 1000,
        populate: '*'
      });
      const providers = response.data || [];
      // Sort: active first, then by name
      const sorted = providers.sort((a: any, b: any) => {
        const aActive = a.attributes?.isActive ?? a.isActive ?? false;
        const bActive = b.attributes?.isActive ?? b.isActive ?? false;
        if (aActive !== bActive) {
          return aActive ? -1 : 1;
        }
        const aName = a.attributes?.name || a.name || '';
        const bName = b.attributes?.name || b.name || '';
        return aName.localeCompare(bName);
      });
      setServiceProviders(sorted);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const loadAllCustomers = async () => {
    try {
      // Load insurance clients
      const insuranceResponse = await insuranceClientService.getAll({
        'pagination[pageSize]': 1000
      });
      const insuranceClientsData = insuranceResponse.data || [];
      setInsuranceClients(insuranceClientsData);

      // Load regular customers
      const customerResponse = await customerService.getAll({
        'pagination[pageSize]': 1000
      });
      const customersData = customerResponse.data || [];
      setRegularCustomers(customersData);

      // Combine both types with a type identifier
      const combined = [
        ...insuranceClientsData.map((client: any) => ({ ...client, _type: 'insurance' })),
        ...customersData.map((customer: any) => ({ ...customer, _type: 'customer' }))
      ];
      setAllCustomers(combined);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleClientSelect = async (client: any) => {
    if (!client) {
      setSelectedClient(null);
      setClientPackageInfo(null);
      setCreateFormData({ ...createFormData, serviceCode: '', serviceType: '' });
      return;
    }

    try {
      setCheckingClient(true);
      setError('');
      const attrs = client.attributes || client;
      const clientType = client._type || (attrs.vinNumber ? 'insurance' : 'customer');
      const vinNumber = attrs.vinNumber;
      const customerId = client.id || attrs.id;
      const customerNumber = attrs.customerNumber;
      
      let result: any = null;

      // Handle insurance clients
      if (clientType === 'insurance' && vinNumber) {
        result = await insuranceClientService.checkVin(vinNumber);
        
        if (result.found) {
          if (!result.policy?.isActive) {
            setError('Client policy has expired or is inactive. Cannot create requests.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          if (!result.client?.servicePackage) {
            setError('Insurance client does not have a service package assigned.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          const availableServices = result.serviceUsage?.services?.filter((s: any) => s.canUseAgain && s.remainingCount > 0);
          if (!availableServices || availableServices.length === 0) {
            setError('No services available. All service limits have been reached.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          setSelectedClient(result.client);
          setClientPackageInfo(result);
          const phone = result.client.phoneNumber || attrs.phoneNumber || '';
          // Extract country code if present, otherwise use default
          if (phone && phone.startsWith('+')) {
            const match = phone.match(/^(\+\d{1,4})(.*)$/);
            if (match) {
              setCountryCode(match[1]);
              setCreateFormData({
                ...createFormData,
                phoneNumber: match[2],
                serviceCode: '',
                serviceType: ''
              });
            } else {
              setCreateFormData({
                ...createFormData,
                phoneNumber: phone,
                serviceCode: '',
                serviceType: ''
              });
            }
          } else {
            setCreateFormData({
              ...createFormData,
              phoneNumber: phone,
              serviceCode: '',
              serviceType: ''
            });
          }
        } else {
          setError('Client information not found');
          setSelectedClient(null);
          setClientPackageInfo(null);
        }
      } 
      // Handle regular customers
      else if (clientType === 'customer') {
        result = await customerService.checkCustomer(customerId, customerNumber, vinNumber);
        
        if (result.found) {
          if (!result.customer?.isActive) {
            setError('Customer account is inactive. Cannot create requests.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          if (!result.customer?.servicePackage) {
            setError('Customer does not have a service package assigned.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          const availableServices = result.serviceUsage?.services?.filter((s: any) => s.canUseAgain && s.remainingCount > 0);
          if (!availableServices || availableServices.length === 0) {
            setError('No services available. All service limits have been reached.');
            setSelectedClient(null);
            setClientPackageInfo(null);
            return;
          }

          setSelectedClient(result.customer);
          setClientPackageInfo(result);
          const phone = result.customer.phone || result.customer.mobile || attrs.phone || attrs.mobile || '';
          // Extract country code if present, otherwise use default
          if (phone && phone.startsWith('+')) {
            const match = phone.match(/^(\+\d{1,4})(.*)$/);
            if (match) {
              setCountryCode(match[1]);
              setCreateFormData({
                ...createFormData,
                phoneNumber: match[2],
                serviceCode: '',
                serviceType: ''
              });
            } else {
              setCreateFormData({
                ...createFormData,
                phoneNumber: phone,
                serviceCode: '',
                serviceType: ''
              });
            }
          } else {
            setCreateFormData({
              ...createFormData,
              phoneNumber: phone,
              serviceCode: '',
              serviceType: ''
            });
          }
        } else {
          setError('Customer information not found');
          setSelectedClient(null);
          setClientPackageInfo(null);
        }
      } else {
        setError('Selected client does not have a VIN number or customer ID');
        setSelectedClient(null);
        setClientPackageInfo(null);
      }
    } catch (error: any) {
      console.error('Error checking client:', error);
      setError(error.response?.data?.error?.message || 'Failed to load client information');
      setSelectedClient(null);
      setClientPackageInfo(null);
    } finally {
      setCheckingClient(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    if (!createFormData.serviceCode) {
      setError('Please select a service');
      return;
    }

    if (!createFormData.phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    if (!locationLatitude || !locationLongitude) {
      setError('Please select a location on the map by clicking on it');
      return;
    }

    try {
      setError('');
      const attrs = selectedClient.attributes || selectedClient;
      const service = clientPackageInfo?.serviceUsage?.services?.find(
        (s: any) => s.serviceCode === createFormData.serviceCode
      );

      // Determine if it's an insurance client or regular customer
      const isInsuranceClient = attrs.vinNumber || clientPackageInfo?.client?.vinNumber;
      
      // Combine country code with phone number
      const fullPhoneNumber = countryCode + createFormData.phoneNumber.replace(/^\+/, '');
      
      const requestData: any = {
        serviceCode: createFormData.serviceCode,
        serviceType: service?.serviceName || createFormData.serviceType,
        description: `${service?.serviceName || createFormData.serviceType}`,
        phoneNumber: fullPhoneNumber,
        status: 'pending'
      };

      // Add location coordinates
      if (locationLatitude !== null && locationLongitude !== null) {
        requestData.locationLatitude = locationLatitude;
        requestData.locationLongitude = locationLongitude;
        // Set locationText to coordinates as fallback
        requestData.locationText = `${locationLatitude.toFixed(6)}, ${locationLongitude.toFixed(6)}`;
      }

      // Set the appropriate client field
      if (isInsuranceClient) {
        requestData.insuranceClient = selectedClient.id;
      } else {
        requestData.customer = selectedClient.id;
      }

      await requestService.create(requestData);
      await loadRequests();
      handleCloseCreateModal();
    } catch (error: any) {
      console.error('Error creating request:', error);
      setError(error.response?.data?.error?.message || 'Failed to create request');
    }
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedClient(null);
    setClientPackageInfo(null);
    setCountryCode('+968');
    setLocationLatitude(null);
    setLocationLongitude(null);
    setMapCenter([23.5859, 58.4059]); // Reset to Muscat, Oman
    setCreateFormData({
      phoneNumber: '',
      serviceCode: '',
      serviceType: ''
    });
    setError('');
  };

  // Component to handle map clicks
  const MapClickHandler: React.FC<{
    onLocationSelect: (lat: number, lng: number) => void;
  }> = ({ onLocationSelect }) => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        onLocationSelect(lat, lng);
      },
    });
    return null;
  };

  const formatSchedule = (schedule: any): string => {
    if (!schedule || typeof schedule !== 'object') return 'No schedule';
    
    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const activeDays = DAYS.filter(day => schedule[day]?.active);
    
    if (activeDays.length === 0) return 'No schedule';
    if (activeDays.length === 7) {
      // Check if all days have same hours
      const firstDay = schedule[activeDays[0]];
      const allSame = activeDays.every(day => 
        schedule[day]?.from === firstDay?.from && schedule[day]?.to === firstDay?.to
      );
      if (allSame && firstDay?.from && firstDay?.to) {
        return `${firstDay.from}-${firstDay.to} (All days)`;
      }
    }
    
    // Show first few active days with times
    const displayDays = activeDays.slice(0, 2);
    const times = displayDays.map(day => {
      const daySchedule = schedule[day];
      if (daySchedule?.from && daySchedule?.to) {
        return `${day.substring(0, 3)} ${daySchedule.from}-${daySchedule.to}`;
      }
      return day.substring(0, 3);
    }).join(', ');
    
    if (activeDays.length > 2) {
      return `${times} +${activeDays.length - 2} more`;
    }
    return times;
  };

  const loadAutoAssignSetting = async () => {
    try {
      const setting = await systemSettingService.getByKey('auto_assign_service_provider');
      if (setting) {
        setAutoAssign(setting.attributes?.value === 'true' || setting.value === 'true');
      }
    } catch (error) {
      console.error('Error loading auto-assign setting:', error);
    }
  };

  const handleAutoAssignToggle = async (checked: boolean) => {
    try {
      setAutoAssignLoading(true);
      await systemSettingService.upsert(
        'auto_assign_service_provider',
        checked.toString(),
        'Automatically assign requests to available service providers',
        'service_management'
      );
      setAutoAssign(checked);
    } catch (error) {
      console.error('Error updating auto-assign setting:', error);
      setError('Failed to update auto-assign setting');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  useEffect(() => {
    if (chatOpen && selectedRequest) {
      loadMessages();
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [chatOpen, selectedRequest, chatTab]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await requestService.getAll({
        'populate[serviceProvider]': '*',
        'populate[insuranceClient]': '*',
        'populate[customer]': '*',
        'populate[invoice]': '*',
        'populate[invoices]': '*',
        'populate[evidencePhotoBefore]': '*',
        'populate[evidencePhotoAfter]': '*',
        sort: 'createdAt:desc',
        'pagination[pageSize]': 1000
      });
      
      const requestsData = response.data || [];
      setRequests(requestsData);
    } catch (err: any) {
      console.error('Error loading requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedRequest) return;
    
    try {
      let phoneNumber = '';
      
      if (chatTab === 'customer') {
        const client = selectedRequest.attributes?.insuranceClient?.data?.attributes || 
                      selectedRequest.attributes?.insuranceClient ||
                      selectedRequest.attributes?.customer?.data?.attributes ||
                      selectedRequest.attributes?.customer;
        phoneNumber = client?.phoneNumber || client?.phone || '';
      } else {
        const provider = selectedRequest.attributes?.serviceProvider?.data?.attributes || 
                        selectedRequest.attributes?.serviceProvider;
        phoneNumber = provider?.phone || '';
      }

      if (!phoneNumber) return;

      const response = await api.get('/whatsapp-messages', {
        params: {
          'filters[phoneNumber][$eq]': phoneNumber.replace(/[^0-9]/g, ''),
          sort: 'sentAt:asc'
        }
      });

      setMessages(response.data?.data || []);
    } catch (err: any) {
      console.error('Error loading messages:', err);
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: 'Pending',
      assigned: 'Assigned',
      active: 'Active',
      invoice_creation: 'Invoice Creation',
      invoice_sent: 'Invoice Sent',
      payment_received: 'Payment Received',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      pending: 'warning',
      assigned: 'primary',
      active: 'primary',
      invoice_creation: 'primary',
      invoice_sent: 'primary',
      payment_received: 'success',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusSeverity = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    const severities: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      pending: 'warning',
      assigned: 'info',
      active: 'info',
      invoice_creation: 'info',
      invoice_sent: 'info',
      payment_received: 'success',
      completed: 'success',
      cancelled: 'error'
    };
    return severities[status] || 'info';
  };

  const getStatusStep = (status: string): number => {
    const steps: Record<string, number> = {
      pending: 0,
      assigned: 1,
      active: 2,
      invoice_creation: 3,
      invoice_sent: 3,
      payment_received: 4,
      completed: 5,
      cancelled: -1
    };
    return steps[status] || 0;
  };

  const categorizeRequests = () => {
    const allCategories: Record<string, Request[]> = {
      pending: [],
      assigned: [],
      active: [],
      invoice_creation: [],
      invoice_sent: [],
      payment_received: [],
      completed: [],
      cancelled: []
    };

    requests.forEach(request => {
      const status = request.attributes?.status || 'pending';
      if (allCategories[status]) {
        allCategories[status].push(request);
      }
    });

    // Create paginated categories for active statuses
    const paginatedCategories: Record<string, Request[]> = {
      pending: [],
      assigned: [],
      active: [],
      invoice_creation: [],
      invoice_sent: [],
      payment_received: [],
      completed: allCategories.completed, // Keep full array for count
      cancelled: allCategories.cancelled  // Keep full array for count
    };

    // Paginate non-completed, non-cancelled statuses
    (['pending', 'assigned', 'active', 'invoice_creation', 'invoice_sent', 'payment_received'] as const).forEach(status => {
      const page = statusPages[status] || 1;
      const startIdx = (page - 1) * ITEMS_PER_PAGE;
      const endIdx = startIdx + ITEMS_PER_PAGE;
      paginatedCategories[status] = allCategories[status].slice(startIdx, endIdx);
    });

    return { paginatedCategories, allCategories };
  };

  // Helper function to get image URL and check if it's valid
  const getImageUrl = (photoData: any): string | null => {
    if (!photoData) return null;
    
    let url = photoData?.data?.attributes?.url || 
              photoData?.url || 
              photoData?.data?.url || 
              photoData?.attributes?.url || 
              '';
    
    // Check if URL is valid (not empty, not null, not undefined)
    if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
      return null;
    }
    
    // If URL is relative (starts with /), prepend the API base URL
    // Strapi uploads are served from the root, not from /api
    if (url.startsWith('/')) {
      const API_URL = process.env.REACT_APP_API_URL || 'https://darb.aiastra.io';
      url = `${API_URL}${url}`;
    }
    
    return url;
  };

  // Helper function to check if image data exists and has valid URL
  const hasValidImage = (photoData: any): boolean => {
    if (!photoData) return false;
    const url = getImageUrl(photoData);
    return url !== null && url !== '';
  };

  const extractFirstUrl = (text?: string | null): string | null => {
    if (!text || typeof text !== 'string') return null;
    const match = text.match(/https?:\/\/[^\s)]+/i);
    return match ? match[0] : null;
  };

  const getRequestLocationHref = (attrs: any): string | null => {
    const explicit =
      extractFirstUrl(attrs?.locationText) ||
      extractFirstUrl(attrs?.description) ||
      extractFirstUrl(attrs?.notes);
    if (explicit) return explicit;

    const lat = attrs?.locationLatitude;
    const lng = attrs?.locationLongitude;
    if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    return null;
  };

  const handleOpenDetails = (request: Request) => {
    setSelectedRequest(request);
    setImageErrors({ before: false, after: false });
    const attrs = request.attributes || request;
    setEditFormData({
      status: attrs.status || 'pending',
      description: attrs.description || '',
      notes: attrs.notes || '',
      toDropDown: attrs.toDropDown || null,
    });
    
    // Initialize edit location from request
    if (attrs.locationLatitude && attrs.locationLongitude) {
      setEditLocationLatitude(attrs.locationLatitude);
      setEditLocationLongitude(attrs.locationLongitude);
      setEditMapCenter([attrs.locationLatitude, attrs.locationLongitude]);
    } else {
      setEditLocationLatitude(null);
      setEditLocationLongitude(null);
      setEditMapCenter([23.5859, 58.4059]);
    }
    
    // Load existing photos
    const beforePhoto = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
    const afterPhoto = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
    if (beforePhoto) {
      const url = getImageUrl(beforePhoto);
      setBeforePhotoPreview(url);
    } else {
      setBeforePhotoPreview(null);
    }
    if (afterPhoto) {
      const url = getImageUrl(afterPhoto);
      setAfterPhotoPreview(url);
    } else {
      setAfterPhotoPreview(null);
    }
    setBeforePhotoFile(null);
    setAfterPhotoFile(null);
    setIsEditing(false);
    setDetailsOpen(true);
    fetchInvoiceItems(request);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRequest(null);
    setIsEditing(false);
    setBeforePhotoFile(null);
    setBeforePhotoPreview(null);
    setAfterPhotoFile(null);
    setAfterPhotoPreview(null);
    setEditLocationLatitude(null);
    setEditLocationLongitude(null);
    setEditMapCenter([23.5859, 58.4059]);
  };

  const reloadRequestDetails = async (requestId: number) => {
    try {
      const response = await requestService.getById(requestId);
      const updatedRequest = response.data;
      setSelectedRequest(updatedRequest);
      // Also update in requests list
      setRequests(prev => prev.map(r => r.id === requestId ? updatedRequest : r));
      await fetchInvoiceItems(updatedRequest);
    } catch (error) {
      console.error('Error reloading request details:', error);
    }
  };

  const fetchInvoiceItems = async (request: any) => {
    try {
      const requestAttrs = request?.attributes ?? request;
      const invoices = requestAttrs.invoices?.data ?? requestAttrs.invoices ?? [];
      const customerInvoice = invoices.find((inv: any) => {
        const invAttrs = inv.attributes ?? inv;
        return invAttrs.invoiceType === 'service_provider_to_darb';
      });
      const insuranceInvoice = invoices.find((inv: any) => {
        const invAttrs = inv.attributes ?? inv;
        return invAttrs.invoiceType === 'insurance_company_monthly';
      });
      if (customerInvoice) {
        const customerInvoiceId = customerInvoice.id ?? customerInvoice.attributes?.id;
        if (customerInvoiceId) {
          try {
            const itemsResponse = await InvoiceService.getItems(customerInvoiceId);
            setCustomerInvoiceItems(itemsResponse.data?.data ?? []);
          } catch (err) {
            console.error('Error fetching customer invoice items:', err);
            setCustomerInvoiceItems([]);
          }
        }
      } else {
        setCustomerInvoiceItems([]);
      }
      if (insuranceInvoice) {
        const insuranceInvoiceId = insuranceInvoice.id ?? insuranceInvoice.attributes?.id;
        if (insuranceInvoiceId) {
          try {
            const itemsResponse = await InvoiceService.getItems(insuranceInvoiceId);
            setInsuranceInvoiceItems(itemsResponse.data?.data ?? []);
          } catch (err) {
            console.error('Error fetching insurance invoice items:', err);
            setInsuranceInvoiceItems([]);
          }
        }
      } else {
        setInsuranceInvoiceItems([]);
      }
      // Legacy single invoice: fetch items and show as customer list
      if (!customerInvoice && !insuranceInvoice) {
        const legacyInv = requestAttrs.invoice?.data ?? requestAttrs.invoice;
        if (legacyInv) {
          const legacyId = legacyInv.id ?? legacyInv.attributes?.id;
          if (legacyId) {
            try {
              const itemsResponse = await InvoiceService.getItems(legacyId);
              setCustomerInvoiceItems(itemsResponse.data?.data ?? []);
            } catch (err) {
              console.error('Error fetching legacy invoice items:', err);
              setCustomerInvoiceItems([]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
      setCustomerInvoiceItems([]);
      setInsuranceInvoiceItems([]);
    }
  };

  const handleEditInvoiceItem = (item: any, invoiceType: 'customer' | 'insurance') => {
    setEditingInvoiceId(null);
    const itemAttrs = item.attributes ?? item;
    const subtotal = (itemAttrs.quantity ?? 1) * (itemAttrs.unitPrice ?? 0);
    const afterDiscount = subtotal - (itemAttrs.discount ?? 0);
    const calculatedTax5Percent = afterDiscount * 0.05;
    const hasTax = itemAttrs.tax ?? 0;
    const isTax5Percent = Math.abs(hasTax - calculatedTax5Percent) < 0.01;
    setEditingInvoiceItem(item);
    setEditingInvoiceType(invoiceType);
    setEditItemFormData({
      description: itemAttrs.description ?? '',
      quantity: itemAttrs.quantity ?? 1,
      unitPrice: itemAttrs.unitPrice ?? 0,
      discount: itemAttrs.discount ?? 0,
      tax: itemAttrs.tax ?? 0,
      total: itemAttrs.total ?? 0,
      apply5PercentTax: isTax5Percent
    });
    setEditInvoiceItemModalOpen(true);
  };

  const handleAddInvoiceItem = (invoiceType: 'customer' | 'insurance', specificInvoiceId?: number) => {
    setEditingInvoiceItem(null);
    setEditingInvoiceType(invoiceType);
    setEditingInvoiceId(specificInvoiceId ?? null);
    setEditItemFormData({
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      total: 0,
      apply5PercentTax: false
    });
    setEditInvoiceItemModalOpen(true);
  };

  const calculateItemTotal = () => {
    const { quantity, unitPrice, discount, apply5PercentTax } = editItemFormData;
    const subtotal = quantity * unitPrice;
    const afterDiscount = subtotal - (discount ?? 0);
    const tax = apply5PercentTax ? afterDiscount * 0.05 : (editItemFormData.tax ?? 0);
    const total = afterDiscount + tax;
    return { total, tax, subtotal, afterDiscount };
  };

  const handleSaveInvoiceItem = async () => {
    if (!selectedRequest) return;
    try {
      setSavingInvoiceItem(true);
      let invoiceId: number | undefined;
      if (editingInvoiceId != null) {
        invoiceId = editingInvoiceId;
      } else {
        const requestAttrs = selectedRequest.attributes ?? selectedRequest;
        const invoices = requestAttrs.invoices?.data ?? requestAttrs.invoices ?? [];
        let invoice = invoices.find((inv: any) => {
          const invAttrs = inv.attributes ?? inv;
          return editingInvoiceType === 'customer'
            ? invAttrs.invoiceType === 'service_provider_to_darb'
            : invAttrs.invoiceType === 'insurance_company_monthly';
        });
        if (!invoice) {
          const legacy = requestAttrs.invoice?.data ?? requestAttrs.invoice;
          invoice = legacy;
        }
        if (!invoice) {
          alert('Invoice not found');
          return;
        }
        invoiceId = invoice.id ?? invoice.attributes?.id;
      }
      if (!invoiceId) {
        alert('Invoice not found');
        return;
      }
      const { total, tax } = calculateItemTotal();
      const itemData = {
        description: editItemFormData.description,
        quantity: editItemFormData.quantity,
        unitPrice: editItemFormData.unitPrice,
        discount: editItemFormData.discount,
        tax,
        total,
        invoice: invoiceId
      };
      if (editingInvoiceItem) {
        const itemId = editingInvoiceItem.id ?? editingInvoiceItem.attributes?.id;
        await InvoiceService.updateItem(itemId, itemData);
      } else {
        await InvoiceService.createItem(itemData);
      }
      if (selectedRequest) {
        await fetchInvoiceItems(selectedRequest);
        const requestId = selectedRequest.id ?? (selectedRequest as any).attributes?.id;
        if (requestId) await reloadRequestDetails(requestId);
      }
      setEditInvoiceItemModalOpen(false);
      setEditingInvoiceItem(null);
      setEditingInvoiceType(null);
      setEditingInvoiceId(null);
      if (invoicesModalOpen) {
        fetchAllInvoices();
      }
    } catch (error) {
      console.error('Error saving invoice item:', error);
      alert('Error saving invoice item');
    } finally {
      setSavingInvoiceItem(false);
    }
  };

  const handleDeleteInvoiceItem = async (item: any, _invoiceType: 'customer' | 'insurance') => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const itemId = item.id ?? item.attributes?.id;
      await InvoiceService.deleteItem(itemId);
      if (selectedRequest) {
        await fetchInvoiceItems(selectedRequest);
        const requestId = selectedRequest.id ?? (selectedRequest as any).attributes?.id;
        if (requestId) await reloadRequestDetails(requestId);
      }
      if (invoicesModalOpen) fetchAllInvoices();
    } catch (error) {
      console.error('Error deleting invoice item:', error);
      alert('Error deleting invoice item');
    }
  };

  useEffect(() => {
    if (editInvoiceItemModalOpen) {
      const { total, tax } = calculateItemTotal();
      setEditItemFormData(prev => ({ ...prev, total, tax }));
    }
  }, [editItemFormData.quantity, editItemFormData.unitPrice, editItemFormData.discount, editItemFormData.apply5PercentTax]);

  const fetchAllInvoices = async () => {
    if (!selectedRequest) return;
    
    setLoadingInvoices(true);
    try {
      const requestAttrs = selectedRequest.attributes || selectedRequest;
      const requestId = selectedRequest.id;
      const serviceProviderId = requestAttrs.serviceProvider?.data?.id || requestAttrs.serviceProvider?.id || requestAttrs.serviceProvider;
      const insuranceClientId = requestAttrs.insuranceClient?.data?.id || requestAttrs.insuranceClient?.id || requestAttrs.insuranceClient;
      const requestNumber = requestAttrs.requestNumber;
      
      console.log('🔍 [FETCH INVOICES] Fetching invoices for request:', requestId, requestNumber);
      console.log('🔍 [FETCH INVOICES] Service Provider ID:', serviceProviderId, 'Insurance Client ID:', insuranceClientId);
      
      // First, try to fetch invoices directly linked to this request
      const params: any = {
        'filters[request][id][$eq]': requestId,
        'populate[serviceProvider]': '*',
        'populate[insuranceClient][populate][insuranceCompany]': '*',
        'populate[invoiceItems]': '*',
        'populate[request]': '*',
      };
      
      let response = await InvoiceService.getAll(params);
      let allInvoices = response.data?.data || [];
      
      console.log('🔍 [FETCH INVOICES] Found', allInvoices.length, 'invoice(s) directly linked to request');
      
      // If no invoices found by request link, try alternative method
      if (allInvoices.length === 0) {
        console.log('🔍 [FETCH INVOICES] No invoices found by request link, trying alternative method...');
        
        // Try fetching by service provider and insurance client
        const altParams: any = {
          'populate[serviceProvider]': '*',
          'populate[insuranceClient][populate][insuranceCompany]': '*',
          'populate[invoiceItems]': '*',
          'populate[request]': '*',
        };
        
        // Add filters
        if (serviceProviderId) {
          altParams['filters[$or][0][serviceProvider][id][$eq]'] = serviceProviderId;
          altParams['filters[$or][0][invoiceType][$eq]'] = 'service_provider_to_darb';
        }
        
        if (insuranceClientId) {
          if (serviceProviderId) {
            altParams['filters[$or][1][insuranceClient][id][$eq]'] = insuranceClientId;
            altParams['filters[$or][1][invoiceType][$eq]'] = 'service_provider_to_darb';
            altParams['filters[$or][2][insuranceClient][id][$eq]'] = insuranceClientId;
            altParams['filters[$or][2][invoiceType][$eq]'] = 'insurance_company_monthly';
          } else {
            altParams['filters[$or][0][insuranceClient][id][$eq]'] = insuranceClientId;
            altParams['filters[$or][0][invoiceType][$eq]'] = 'service_provider_to_darb';
            altParams['filters[$or][1][insuranceClient][id][$eq]'] = insuranceClientId;
            altParams['filters[$or][1][invoiceType][$eq]'] = 'insurance_company_monthly';
          }
        }
        
        // Also filter by notes containing request number
        if (requestNumber) {
          altParams['filters[$or][3][notes][$containsi]'] = requestNumber;
        }
        
        response = await InvoiceService.getAll(altParams);
        allInvoices = response.data?.data || [];
        console.log('🔍 [FETCH INVOICES] Found', allInvoices.length, 'invoice(s) by alternative method');
      }
      
      // Debug: Log all invoices to see their structure
      console.log('🔍 [FETCH INVOICES] All invoices found:', allInvoices.map((inv: any) => {
        const invAttrs = inv.attributes || inv;
        const spId = invAttrs.serviceProvider?.data?.id || invAttrs.serviceProvider?.id || invAttrs.serviceProvider;
        const icId = invAttrs.insuranceClient?.data?.id || invAttrs.insuranceClient?.id || invAttrs.insuranceClient;
        const reqId = invAttrs.request?.data?.id || invAttrs.request?.id || invAttrs.request;
        return {
          invoiceNumber: invAttrs.invoiceNumber,
          invoiceType: invAttrs.invoiceType,
          serviceProvider: spId || 'null',
          insuranceClient: icId || 'null',
          request: reqId || 'null',
          hasServiceProvider: !!spId,
          hasInsuranceClient: !!icId
        };
      }));
      
      // Filter invoices linked to this request first
      const requestInvoices = allInvoices.filter((inv: any) => {
        const invAttrs = inv.attributes || inv;
        const invRequestId = invAttrs.request?.data?.id || invAttrs.request?.id || invAttrs.request;
        return requestId && invRequestId && (invRequestId === requestId || invRequestId.toString() === requestId.toString());
      });
      
      console.log('🔍 [FETCH INVOICES] Invoices linked to request:', requestInvoices.length);
      
      // Find service provider invoice (service_provider_to_darb with serviceProvider)
      const providerInv = (requestInvoices.length > 0 ? requestInvoices : allInvoices).find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        const invServiceProviderId = invAttrs.serviceProvider?.data?.id || invAttrs.serviceProvider?.id || invAttrs.serviceProvider;
        return invAttrs.invoiceType === 'service_provider_to_darb' && 
               invServiceProviderId && 
               invServiceProviderId.toString() === serviceProviderId?.toString();
      });
      
      // Find client invoice (service_provider_to_darb with insuranceClient, NO serviceProvider)
      // This is the key: client invoice has insuranceClient but NO serviceProvider field set
      const clientInv = (requestInvoices.length > 0 ? requestInvoices : allInvoices).find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        const invInsuranceClientId = invAttrs.insuranceClient?.data?.id || invAttrs.insuranceClient?.id || invAttrs.insuranceClient;
        const invServiceProvider = invAttrs.serviceProvider;
        const invServiceProviderData = invServiceProvider?.data;
        const invServiceProviderId = invServiceProviderData?.id || invServiceProvider?.id || invServiceProvider;
        
        // Must be service_provider_to_darb type
        if (invAttrs.invoiceType !== 'service_provider_to_darb') return false;
        
        // Must have insurance client matching
        if (!invInsuranceClientId || invInsuranceClientId.toString() !== insuranceClientId?.toString()) return false;
        
        // Must NOT have service provider (this is the key difference from provider invoice)
        // Check if serviceProvider.data is null or if serviceProviderId doesn't exist
        const hasServiceProvider = invServiceProviderData && 
                                   invServiceProviderData !== null &&
                                   invServiceProviderId && 
                                   invServiceProviderId !== null &&
                                   invServiceProviderId.toString() !== 'null' && 
                                   invServiceProviderId.toString() !== '';
        
        const isClientInvoice = !hasServiceProvider;
        
        if (isClientInvoice) {
          console.log('✅ [FETCH INVOICES] Found client invoice:', invAttrs.invoiceNumber, {
            invoiceType: invAttrs.invoiceType,
            hasInsuranceClient: !!invInsuranceClientId,
            hasServiceProvider: hasServiceProvider,
            serviceProviderData: invServiceProviderData,
            serviceProviderId: invServiceProviderId
          });
        }
        
        return isClientInvoice;
      });
      
      // Find insurance invoice (insurance_company_monthly)
      const insuranceInv = (requestInvoices.length > 0 ? requestInvoices : allInvoices).find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        const invInsuranceClientId = invAttrs.insuranceClient?.data?.id || invAttrs.insuranceClient?.id || invAttrs.insuranceClient;
        return invAttrs.invoiceType === 'insurance_company_monthly' &&
               invInsuranceClientId &&
               invInsuranceClientId.toString() === insuranceClientId?.toString();
      });
      
      console.log('✅ [FETCH INVOICES] Found invoices:', {
        provider: providerInv ? (providerInv.attributes || providerInv).invoiceNumber : 'none',
        client: clientInv ? (clientInv.attributes || clientInv).invoiceNumber : 'none',
        insurance: insuranceInv ? (insuranceInv.attributes || insuranceInv).invoiceNumber : 'none'
      });
      
      setProviderInvoice(providerInv || null);
      setClientInvoice(clientInv || null);
      setInsuranceInvoice(insuranceInv || null);
      const loadItems = async (inv: any, setter: (items: any[]) => void) => {
        if (!inv) { setter([]); return; }
        const id = inv.id ?? inv.attributes?.id;
        if (!id) { setter([]); return; }
        try {
          const res = await InvoiceService.getItems(id);
          setter(res.data?.data ?? []);
        } catch {
          setter([]);
        }
      };
      await loadItems(providerInv, setProviderInvoiceItems);
      await loadItems(clientInv, setClientInvoiceItems);
      await loadItems(insuranceInv, setInsuranceInvoiceItemsAllModal);
    } catch (error) {
      console.error('❌ [FETCH INVOICES] Error fetching invoices:', error);
      setProviderInvoice(null);
      setClientInvoice(null);
      setInsuranceInvoice(null);
      setProviderInvoiceItems([]);
      setClientInvoiceItems([]);
      setInsuranceInvoiceItemsAllModal([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleOpenInvoicesModal = () => {
    setInvoicesModalOpen(true);
    fetchAllInvoices();
  };

  const handleDownloadInvoicePDF = async (invoiceType: 'customer' | 'insurance' = 'customer') => {
    if (!selectedRequest) return;
    const requestAttrs = selectedRequest.attributes ?? selectedRequest;
    const invoices = requestAttrs.invoices?.data ?? requestAttrs.invoices ?? [];
    let targetInvoice = invoices.find((inv: any) => {
      const invAttrs = inv.attributes ?? inv;
      return invoiceType === 'customer'
        ? invAttrs.invoiceType === 'service_provider_to_darb'
        : invAttrs.invoiceType === 'insurance_company_monthly';
    });
    if (!targetInvoice) {
      targetInvoice = selectedRequest.attributes?.invoice?.data ?? selectedRequest.attributes?.invoice;
    }
    if (!targetInvoice) return;
    const invoiceId = targetInvoice.id ?? targetInvoice.attributes?.id;
    if (!invoiceId) return;

    try {
      // Fetch invoice with full details

      const invoiceResponse = await InvoiceService.getOne(invoiceId, {
        populate: ['customer', 'insuranceClient.insuranceCompany', 'relatedRequests'],
      });
      const invoice = invoiceResponse.data?.data;
      if (!invoice) return;

      // Fetch line items
      const itemsResponse = await InvoiceService.getItems(invoiceId);
      const lineItems = itemsResponse.data?.data || [];

      const doc = new jsPDF();
      const data = invoice.attributes || invoice;
      const insuranceClient = data.insuranceClient?.data?.attributes || data.customer?.data?.attributes;
      const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany;

      // Load and add logo
      await addLogoToPDF(doc);

      // Add company name
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

      // Add invoice type header
      if (invoiceType === 'insurance') {
        doc.setFontSize(14);
        doc.setTextColor(33, 150, 243);
        doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
      }

      // Add invoice details on the right
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
      };
      doc.text(`DATE: ${formatDate(data.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
      doc.text(`INVOICE #: ${data.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
      doc.text(`Status: ${(data.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

      // Add company details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let companyY = invoiceType === 'insurance' ? 58 : 48;
      doc.text('CR: 1366462', 20, companyY);
      companyY += 5;
      doc.text('BLOCK 255, STREET, WAY 5508, BUILDING 564', 20, companyY);
      companyY += 5;
      doc.text('VAT NUM: OM1100429932', 20, companyY);
      companyY += 5;
      if (data.omanTaxId) {
        doc.text(`Oman Tax ID: ${data.omanTaxId}`, 20, companyY);
        companyY += 5;
      }
      doc.text('Phone: 80081818 - 24790888', 20, companyY);
      companyY += 5;
      doc.text('Email: INFO@DARBNS.COM', 20, companyY);

      // Add insurance client information
      let billToY = (invoiceType === 'insurance' ? 90 : 75) + (data.omanTaxId ? 5 : 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('BILL TO:', 20, billToY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      let yPos = billToY + 7;
      doc.text(insuranceClient?.customerName || insuranceClient?.companyName || insuranceClient?.contactPerson || '-', 20, yPos);
      
      // Insurance invoice specific fields
      if (invoiceType === 'insurance') {
        if (insuranceCompany?.name) {
          yPos += 5;
          doc.setFont('helvetica', 'bold');
          doc.text(`Insurance Company: ${insuranceCompany.name}`, 20, yPos);
          doc.setFont('helvetica', 'normal');
        }
        if (insuranceClient?.policyNumber) {
          yPos += 5;
          doc.text(`Policy Number: ${insuranceClient.policyNumber}`, 20, yPos);
        }
        if (insuranceClient?.vinNumber) {
          yPos += 5;
          doc.text(`VIN Number: ${insuranceClient.vinNumber}`, 20, yPos);
        }
      }
      
      if (insuranceClient?.email) {
        yPos += 5;
        doc.text(`Email: ${insuranceClient.email}`, 20, yPos);
      }
      if (insuranceClient?.phoneNumber || insuranceClient?.phone) {
        yPos += 5;
        doc.text(`Phone: ${insuranceClient.phoneNumber || insuranceClient.phone}`, 20, yPos);
      }
      if (invoiceType === 'customer' && insuranceClient?.vinNumber) {
        yPos += 5;
        doc.text(`VIN: ${insuranceClient.vinNumber}`, 20, yPos);
      }
      if (insuranceClient?.address) {
        yPos += 5;
        doc.text(`Address: ${insuranceClient.address}`, 20, yPos);
      }

      // Get request data for service information
      const requestAttrs = selectedRequest.attributes || selectedRequest;
      const serviceCode = requestAttrs.serviceCode;
      const serviceNames: { [key: string]: string } = {
        'TOWING_SERVICE': 'Vehicle Towing',
        'BATTERY_BOOST': 'Battery Boost',
        'LOCKOUT_SERVICE': 'Lockout Assistance',
        'FUEL_DELIVERY': 'Fuel Delivery',
      };
      const serviceName = serviceCode ? serviceNames[serviceCode] || serviceCode : '';

      // Add line items table
      const tableData = lineItems.map((item: any) => {
        const attrs = item.attributes || item;
        const product = attrs?.product?.data?.attributes || attrs?.product;
        const description = product?.name || attrs?.description || '-';
        const unitPrice = attrs?.unitPrice || 0;
        
        if (invoiceType === 'insurance') {
          return [
            description,
            serviceName || '-',
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(attrs?.total || 0).toFixed(2)}`,
          ];
        } else {
          return [
            description,
            attrs?.quantity || 0,
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(attrs?.discount || 0).toFixed(2)}`,
            `OMR ${(attrs?.tax || 0).toFixed(2)}`,
            `OMR ${(attrs?.total || 0).toFixed(2)}`,
          ];
        }
      });

      const tableHeaders = invoiceType === 'insurance' 
        ? [['DESCRIPTION', 'SERVICE', 'RATE', 'AMOUNT']]
        : [['Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']];

      autoTable(doc, {
        startY: yPos + 10,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [33, 150, 243],
          textColor: 255,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 9,
        },
        columnStyles: invoiceType === 'insurance' 
          ? {
              0: { cellWidth: 60 },
              1: { cellWidth: 40 },
              2: { halign: 'right', cellWidth: 30 },
              3: { halign: 'right', cellWidth: 30 },
            }
          : {
              0: { cellWidth: 60 },
              1: { halign: 'right', cellWidth: 20 },
              2: { halign: 'right', cellWidth: 25 },
              3: { halign: 'right', cellWidth: 25 },
              4: { halign: 'right', cellWidth: 25 },
              5: { halign: 'right', cellWidth: 25 },
            },
      });

      // Add totals
      const finalY = (doc as any).lastAutoTable.finalY || yPos + 10;
      doc.setFontSize(10);
      
      doc.text('Subtotal:', 130, finalY + 10);
      doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
      
      if (invoiceType === 'customer') {
        doc.text('Discount:', 130, finalY + 17);
        doc.text(`- OMR ${(data.discount || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
      }
      
      doc.text('Tax:', 130, finalY + (invoiceType === 'customer' ? 24 : 17));
      doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 24 : 17), { align: 'right' });
      
      // Total line
      doc.setDrawColor(33, 150, 243);
      doc.setLineWidth(0.5);
      doc.line(130, finalY + (invoiceType === 'customer' ? 27 : 20), 180, finalY + (invoiceType === 'customer' ? 27 : 20));
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', 130, finalY + (invoiceType === 'customer' ? 34 : 27));
      doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 34 : 27), { align: 'right' });

      // Payment info
      if (data.paidAmount > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Paid Amount:', 130, finalY + (invoiceType === 'customer' ? 44 : 37));
        doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 44 : 37), { align: 'right' });
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0);
        doc.text('Balance Due:', 130, finalY + (invoiceType === 'customer' ? 51 : 44));
        doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 51 : 44), { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }

      // Add notes if any
      if (data.notes) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, finalY + (invoiceType === 'customer' ? 65 : 58));
        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(data.notes, 170);
        doc.text(splitNotes, 20, finalY + (invoiceType === 'customer' ? 72 : 65));
      }

      // Save the PDF
      const fileName = invoiceType === 'insurance' 
        ? `Insurance-Invoice-${data.invoiceNumber}.pdf`
        : `Customer-Invoice-${data.invoiceNumber}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      alert('Failed to download invoice PDF');
    }
  };

  const handleDownloadInvoicePDFById = async (invoiceId: number, invoiceType: 'customer' | 'insurance' = 'customer') => {
    try {
      const invoiceResponse = await InvoiceService.getOne(invoiceId, {
        populate: ['customer', 'insuranceClient.insuranceCompany', 'relatedRequests'],
      });
      const invoice = invoiceResponse.data?.data;
      if (!invoice) return;

      // Fetch line items
      const itemsResponse = await InvoiceService.getItems(invoiceId);
      const lineItems = itemsResponse.data?.data || [];

      const doc = new jsPDF();
      const data = invoice.attributes || invoice;
      const insuranceClient = data.insuranceClient?.data?.attributes || data.customer?.data?.attributes;
      const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany;

      // Load and add logo
      await addLogoToPDF(doc);

      // Add company name
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

      // Add invoice type header
      if (invoiceType === 'insurance') {
        doc.setFontSize(14);
        doc.setTextColor(33, 150, 243);
        doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
      }

      // Add invoice details on the right
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
      };
      doc.text(`DATE: ${formatDate(data.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
      doc.text(`INVOICE #: ${data.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
      doc.text(`Status: ${(data.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

      // Add company details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let companyY = invoiceType === 'insurance' ? 58 : 48;
      doc.text('CR: 1366462', 20, companyY);
      companyY += 5;
      doc.text('BLOCK 255, STREET, WAY 5508, BUILDING 564', 20, companyY);
      companyY += 5;
      doc.text('VAT NUM: OM1100429932', 20, companyY);
      companyY += 5;
      if (data.omanTaxId) {
        doc.text(`Oman Tax ID: ${data.omanTaxId}`, 20, companyY);
        companyY += 5;
      }
      doc.text('Phone: 80081818 - 24790888', 20, companyY);
      companyY += 5;
      doc.text('Email: INFO@DARBNS.COM', 20, companyY);

      // Add insurance client information
      let billToY = (invoiceType === 'insurance' ? 90 : 75) + (data.omanTaxId ? 5 : 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('BILL TO:', 20, billToY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      let yPos = billToY + 7;
      doc.text(insuranceClient?.customerName || insuranceClient?.companyName || insuranceClient?.contactPerson || '-', 20, yPos);
      
      // Insurance invoice specific fields
      if (invoiceType === 'insurance') {
        if (insuranceCompany?.name) {
          yPos += 5;
          doc.text(`Insurance Company: ${insuranceCompany.name}`, 20, yPos);
        }
      }

      // Add invoice items table
      const tableData = lineItems.map((item: any) => {
        const itemData = item.attributes || item;
        return [
          itemData.description || '-',
          (itemData.quantity || 1).toString(),
          `OMR ${(itemData.unitPrice || 0).toFixed(3)}`,
          `OMR ${(itemData.total || 0).toFixed(3)}`
        ];
      });

      const tableHeaders = invoiceType === 'insurance' 
        ? ['Description', 'Qty', 'Unit Price', 'Amount']
        : ['Description', 'Qty', 'Unit Price', 'Amount'];

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: invoiceType === 'insurance' ? 110 : 100,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255, fontStyle: 'bold' },
        columnStyles: invoiceType === 'insurance' 
          ? { 0: { cellWidth: 80 }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 30, halign: 'right' }, 3: { cellWidth: 30, halign: 'right' } }
          : { 0: { cellWidth: 80 }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 30, halign: 'right' }, 3: { cellWidth: 30, halign: 'right' } },
        margin: { left: 20, right: 20 }
      });

      const finalY = (doc as any).lastAutoTable.finalY || (invoiceType === 'insurance' ? 110 : 100);

      // Add totals
      if (invoiceType === 'customer') {
        doc.text('Subtotal:', 130, finalY + 17);
        doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
        doc.text('Tax:', 130, finalY + 24);
        doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + 24, { align: 'right' });
        doc.line(130, finalY + 27, 180, finalY + 27);
        doc.text('Total:', 130, finalY + 34);
        doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + 34, { align: 'right' });
        if (data.paidAmount && data.paidAmount > 0) {
          doc.text('Paid Amount:', 130, finalY + 44);
          doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + 44, { align: 'right' });
          doc.text('Balance Due:', 130, finalY + 51);
          doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + 51, { align: 'right' });
        }
      } else {
        doc.text('Subtotal:', 130, finalY + 10);
        doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
        doc.line(130, finalY + 13, 180, finalY + 13);
        doc.text('Total:', 130, finalY + 20);
        doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + 20, { align: 'right' });
        if (data.paidAmount && data.paidAmount > 0) {
          doc.text('Paid Amount:', 130, finalY + 30);
          doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + 30, { align: 'right' });
          doc.text('Balance Due:', 130, finalY + 37);
          doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + 37, { align: 'right' });
        }
      }

      // Add notes if available
      if (data.notes) {
        const notesY = finalY + (invoiceType === 'customer' ? 65 : 58);
        doc.setFontSize(9);
        doc.text('Notes:', 20, notesY);
        const splitNotes = doc.splitTextToSize(data.notes, 160);
        doc.text(splitNotes, 20, notesY + 7);
      }

      const fileName = invoiceType === 'insurance' 
        ? `Insurance-Invoice-${data.invoiceNumber}.pdf`
        : `Customer-Invoice-${data.invoiceNumber}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
    }
  };

  const uploadFile = async (file: File): Promise<number | null> => {
    const formData = new FormData();
    formData.append('files', file);

    try {
      // Axios interceptor will handle removing Content-Type for FormData
      const response = await api.post('/upload', formData);

      console.log('Upload response:', response.data);

      // Handle different response structures
      let uploadedFile = null;
      if (Array.isArray(response.data)) {
        uploadedFile = response.data[0];
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        uploadedFile = response.data.data[0];
      } else if (response.data?.data) {
        uploadedFile = response.data.data;
      } else {
        uploadedFile = response.data;
      }

      if (uploadedFile) {
        // Get ID from different possible structures
        const fileId = uploadedFile.id || uploadedFile.data?.id || uploadedFile.attributes?.id;
        if (fileId) {
          console.log('Uploaded file ID:', fileId);
          return fileId;
        }
      }

      console.error('Unexpected upload response structure:', response.data);
      return null;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      if (error.response) {
        console.error('Upload error response:', error.response.data);
        throw new Error(error.response.data?.error?.message || 'Failed to upload file');
      } else {
        throw new Error('Failed to upload file. Please try again.');
      }
    }
  };

  // Handle admin upload of before photo (in view mode)
  const handleAdminBeforePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent any default behavior that might cause page reload
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (!file || !selectedRequest) {
      console.log('No file selected or no request selected');
      // Reset input if no file
      if (e.target) e.target.value = '';
      return;
    }

    // Store the input element reference
    const inputElement = e.target;
    const requestId = selectedRequest.id;

    try {
      setUploading(true);
      setError('');
      
      console.log('[BEFORE PHOTO UPLOAD] Starting upload...', { fileName: file.name, fileSize: file.size, fileType: file.type, requestId });
      const photoId = await uploadFile(file);
      
      if (photoId) {
        console.log('[BEFORE PHOTO UPLOAD] Upload successful, photo ID:', photoId);
        console.log('[BEFORE PHOTO UPLOAD] Updating request with photo ID...');
        
        await requestService.update(requestId, { evidencePhotoBefore: photoId } as any);
        console.log('[BEFORE PHOTO UPLOAD] Request updated, reloading requests...');
        
        await loadRequests();
        console.log('[BEFORE PHOTO UPLOAD] Requests reloaded, finding updated request...');
        
        // Reload the selected request to get fresh data
        const response = await requestService.getById(requestId);
        if (response.data) {
          setSelectedRequest(response.data);
          setImageErrors({ before: false, after: false });
          console.log('[BEFORE PHOTO UPLOAD] Request updated successfully, photo visible');
        }
      } else {
        throw new Error('Upload failed - no photo ID returned');
      }
    } catch (error: any) {
      console.error('[BEFORE PHOTO UPLOAD] Error uploading before photo:', error);
      console.error('[BEFORE PHOTO UPLOAD] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.message || error.response?.data?.error?.message || 'Failed to upload before photo';
      setError(errorMessage);
      console.log('[BEFORE PHOTO UPLOAD] Error message set:', errorMessage);
    } finally {
      setUploading(false);
      // Reset input after a small delay to ensure state updates
      setTimeout(() => {
        if (inputElement) inputElement.value = '';
        console.log('[BEFORE PHOTO UPLOAD] Input reset, upload process complete');
      }, 100);
    }
  };

  // Handle admin upload of after photo (in view mode)
  const handleAdminAfterPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent any default behavior that might cause page reload
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (!file || !selectedRequest) {
      console.log('No file selected or no request selected');
      // Reset input if no file
      if (e.target) e.target.value = '';
      return;
    }

    // Store the input element reference
    const inputElement = e.target;
    const requestId = selectedRequest.id;

    try {
      setUploading(true);
      setError('');
      
      console.log('[AFTER PHOTO UPLOAD] Starting upload...', { fileName: file.name, fileSize: file.size, fileType: file.type, requestId });
      const photoId = await uploadFile(file);
      
      if (photoId) {
        console.log('[AFTER PHOTO UPLOAD] Upload successful, photo ID:', photoId);
        console.log('[AFTER PHOTO UPLOAD] Updating request with photo ID...');
        
        await requestService.update(requestId, { evidencePhotoAfter: photoId } as any);
        console.log('[AFTER PHOTO UPLOAD] Request updated, reloading requests...');
        
        await loadRequests();
        console.log('[AFTER PHOTO UPLOAD] Requests reloaded, finding updated request...');
        
        // Reload the selected request to get fresh data
        const response = await requestService.getById(requestId);
        if (response.data) {
          setSelectedRequest(response.data);
          setImageErrors({ before: false, after: false });
          console.log('[AFTER PHOTO UPLOAD] Request updated successfully, photo visible');
        }
      } else {
        throw new Error('Upload failed - no photo ID returned');
      }
    } catch (error: any) {
      console.error('[AFTER PHOTO UPLOAD] Error uploading after photo:', error);
      console.error('[AFTER PHOTO UPLOAD] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.message || error.response?.data?.error?.message || 'Failed to upload after photo';
      setError(errorMessage);
      console.log('[AFTER PHOTO UPLOAD] Error message set:', errorMessage);
    } finally {
      setUploading(false);
      // Reset input after a small delay to ensure state updates
      setTimeout(() => {
        if (inputElement) inputElement.value = '';
        console.log('[AFTER PHOTO UPLOAD] Input reset, upload process complete');
      }, 100);
    }
  };

  const handleBeforePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBeforePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBeforePhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAfterPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAfterPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAfterPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBeforePhoto = () => {
    setBeforePhotoFile(null);
    setBeforePhotoPreview(null);
  };

  const removeAfterPhoto = () => {
    setAfterPhotoFile(null);
    setAfterPhotoPreview(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedRequest) return;

    try {
      setUploading(true);
      setError('');

      // Upload files if new ones are selected
      let beforePhotoId: number | null = null;
      let afterPhotoId: number | null = null;

      if (beforePhotoFile) {
        beforePhotoId = await uploadFile(beforePhotoFile);
        if (!beforePhotoId) {
          setError('Failed to upload before photo');
          setUploading(false);
          return;
        }
      } else if (beforePhotoPreview && !beforePhotoFile) {
        // Keep existing photo if not changed
        const attrs = selectedRequest.attributes || selectedRequest;
        const existingBefore = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
        if (existingBefore) {
          beforePhotoId = existingBefore.id || existingBefore.attributes?.id;
        }
      }

      if (afterPhotoFile) {
        afterPhotoId = await uploadFile(afterPhotoFile);
        if (!afterPhotoId) {
          setError('Failed to upload after photo');
          setUploading(false);
          return;
        }
      } else if (afterPhotoPreview && !afterPhotoFile) {
        // Keep existing photo if not changed
        const attrs = selectedRequest.attributes || selectedRequest;
        const existingAfter = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
        if (existingAfter) {
          afterPhotoId = existingAfter.id || existingAfter.attributes?.id;
        }
      }

      const dataToSubmit: any = {
        ...editFormData
      };

      // Add location data if updated
      if (editLocationLatitude !== null && editLocationLongitude !== null) {
        dataToSubmit.locationLatitude = editLocationLatitude;
        dataToSubmit.locationLongitude = editLocationLongitude;
        if (!dataToSubmit.locationText) {
          dataToSubmit.locationText = `${editLocationLatitude.toFixed(6)}, ${editLocationLongitude.toFixed(6)}`;
        }
      }

      if (beforePhotoId !== null) {
        dataToSubmit.evidencePhotoBefore = beforePhotoId;
      }
      if (afterPhotoId !== null) {
        dataToSubmit.evidencePhotoAfter = afterPhotoId;
      }

      await requestService.update(selectedRequest.id, dataToSubmit);
      await loadRequests();
      
      // Reload the selected request
      const updated = requests.find(r => r.id === selectedRequest.id);
      if (updated) {
        setSelectedRequest(updated);
        const attrs = updated.attributes || updated;
        setEditFormData({
          status: attrs.status || 'pending',
          description: attrs.description || '',
          notes: attrs.notes || '',
          toDropDown: attrs.toDropDown || null,
        });
      }
      
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving request:', err);
      setError(err.response?.data?.error?.message || 'Failed to save request');
    } finally {
      setUploading(false);
    }
  };

  const handleAssignDialogOpen = (request: Request) => {
    setAssigningRequest(request);
    setProviderToAssign(null);
    setAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setAssigningRequest(null);
    setProviderToAssign(null);
  };

  const handleAssignProvider = async () => {
    if (!assigningRequest || !providerToAssign) {
      setError('Please select a service provider');
      return;
    }

    try {
      await requestService.assignProvider(assigningRequest.id, providerToAssign.id);
      await loadRequests();
      if (detailsOpen && selectedRequest && selectedRequest.id === assigningRequest.id) {
        const updated = requests.find(r => r.id === assigningRequest.id);
        if (updated) {
          setSelectedRequest(updated);
        }
      }
      handleAssignDialogClose();
    } catch (error: any) {
      console.error('Error assigning provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to assign provider');
    }
  };

  const handleChangeProviderOpen = (request: Request) => {
    setChangingRequest(request);
    setNewProvider(null);
    setChangeProviderOpen(true);
  };

  const handleAssignClientOpen = (request: Request) => {
    setAssigningRequestForClient(request);
    setSelectedClientToAssign(null);
    setCreatingNewClient(false);
    setNewClientFormData({
      vinNumber: '',
      customerName: '',
      phoneNumber: '',
      email: '',
      plateNumber: '',
      carMake: '',
      carModel: '',
      carYear: '',
      policyNumber: '',
      policyStartDate: '',
      policyEndDate: '',
      servicePackage: '',
      insuranceCompany: '',
      notes: '',
      isActive: true
    });
    // Extract VIN from request description or notes if available
    const attrs = request.attributes || request;
    const description = attrs.description || '';
    const notes = attrs.notes || '';
    const vinMatch = description.match(/VIN[:\s]*([A-HJ-NPR-Z0-9]{17})/i) || 
                     notes.match(/VIN[:\s]*([A-HJ-NPR-Z0-9]{17})/i);
    if (vinMatch) {
      setNewClientFormData(prev => ({ ...prev, vinNumber: vinMatch[1] }));
    }
    setAssignClientModalOpen(true);
  };

  const handleAssignClientClose = () => {
    setAssignClientModalOpen(false);
    setAssigningRequestForClient(null);
    setSelectedClientToAssign(null);
    setCreatingNewClient(false);
    setNewClientFormData({
      vinNumber: '',
      customerName: '',
      phoneNumber: '',
      email: '',
      plateNumber: '',
      carMake: '',
      carModel: '',
      carYear: '',
      policyNumber: '',
      policyStartDate: '',
      policyEndDate: '',
      servicePackage: '',
      insuranceCompany: '',
      notes: '',
      isActive: true
    });
  };

  const handleCreateAndAssignClient = async () => {
    if (!assigningRequestForClient) return;

    try {
      setAssigningClient(true);
      setError('');

      // Validate required fields
      if (!newClientFormData.vinNumber || !newClientFormData.customerName) {
        setError('VIN Number and Customer Name are required');
        setAssigningClient(false);
        return;
      }

      // Create new insurance client
      const clientData = {
        vinNumber: newClientFormData.vinNumber.trim(),
        customerName: newClientFormData.customerName.trim(),
        phoneNumber: newClientFormData.phoneNumber || null,
        email: newClientFormData.email || null,
        plateNumber: newClientFormData.plateNumber || null,
        carMake: newClientFormData.carMake || null,
        carModel: newClientFormData.carModel || null,
        carYear: newClientFormData.carYear || null,
        policyNumber: newClientFormData.policyNumber || null,
        policyStartDate: newClientFormData.policyStartDate || null,
        policyEndDate: newClientFormData.policyEndDate || null,
        servicePackage: newClientFormData.servicePackage || null,
        insuranceCompany: newClientFormData.insuranceCompany || null,
        notes: newClientFormData.notes || null,
        isActive: newClientFormData.isActive
      };

      const newClient = await insuranceClientService.create(clientData);

      // Clean up notes - remove NOT OUR CLIENT message
      const attrs = assigningRequestForClient.attributes || assigningRequestForClient;
      let cleanedNotes = attrs.notes || '';
      if (cleanedNotes) {
        // Remove the NOT OUR CLIENT message (using [\s\S] instead of . with s flag for compatibility)
        cleanedNotes = cleanedNotes
          .replace(/🔴 NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/VIN number was provided but this client is NOT in our database\. This is NOT our client\. Sales team must contact customer before assignment\./g, '')
          .trim();
      }

      // Assign client to request and remove flags
      await requestService.update(assigningRequestForClient.id, {
        insuranceClient: newClient.id || newClient.data?.id,
        isNotOurClient: false,
        notes: cleanedNotes || null
      } as any);

      // Reload requests
      await loadRequests();
      
      // Close modal and details
      handleAssignClientClose();
      handleCloseDetails();
      
      setError('');
    } catch (error: any) {
      console.error('Error creating and assigning client:', error);
      setError(error.response?.data?.error?.message || error.message || 'Failed to create and assign client');
    } finally {
      setAssigningClient(false);
    }
  };

  const handleAssignExistingClient = async () => {
    if (!assigningRequestForClient || !selectedClientToAssign) return;

    try {
      setAssigningClient(true);
      setError('');

      const clientId = selectedClientToAssign.id || 
                      selectedClientToAssign.data?.id || 
                      selectedClientToAssign.attributes?.id;

      if (!clientId) {
        setError('Invalid client selected');
        setAssigningClient(false);
        return;
      }

      // Clean up notes - remove NOT OUR CLIENT message
      const attrs = assigningRequestForClient.attributes || assigningRequestForClient;
      let cleanedNotes = attrs.notes || '';
      if (cleanedNotes) {
        // Remove the NOT OUR CLIENT message (using [\s\S] instead of . with s flag for compatibility)
        cleanedNotes = cleanedNotes
          .replace(/🔴 NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/VIN number was provided but this client is NOT in our database\. This is NOT our client\. Sales team must contact customer before assignment\./g, '')
          .trim();
      }

      // Assign client to request and remove flags
      await requestService.update(assigningRequestForClient.id, {
        insuranceClient: clientId,
        isNotOurClient: false,
        notes: cleanedNotes || null
      } as any);

      // Reload requests
      await loadRequests();
      
      // Close modal and details
      handleAssignClientClose();
      handleCloseDetails();
      
      setError('');
    } catch (error: any) {
      console.error('Error assigning client:', error);
      setError(error.response?.data?.error?.message || error.message || 'Failed to assign client');
    } finally {
      setAssigningClient(false);
    }
  };

  const handleChangeProviderClose = () => {
    setChangeProviderOpen(false);
    setChangingRequest(null);
    setNewProvider(null);
  };

  const handleChangeProvider = async () => {
    if (!changingRequest || !newProvider) {
      setError('Please select a service provider');
      return;
    }

    const reason = prompt('Please provide reason for changing service provider:');
    if (!reason) return;

    try {
      await requestService.changeProvider(changingRequest.id, newProvider.id, reason);
      await loadRequests();
      if (detailsOpen && selectedRequest && selectedRequest.id === changingRequest.id) {
        const updated = requests.find(r => r.id === changingRequest.id);
        if (updated) {
          setSelectedRequest(updated);
        }
      }
      handleChangeProviderClose();
    } catch (error: any) {
      console.error('Error changing provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to change provider');
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const updateData: any = { status: newStatus };
      
      if (newStatus === 'active' && !updateData.assignedAt) {
        const request = requests.find(r => r.id === id);
        const attrs = request?.attributes || request;
        if (!attrs?.assignedAt) {
          updateData.assignedAt = new Date().toISOString();
        }
      } else if (newStatus === 'invoice_sent') {
        updateData.invoiceSentAt = new Date().toISOString();
      } else if (newStatus === 'payment_received') {
        updateData.paymentReceivedAt = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completedDate = new Date().toISOString();
      } else if (newStatus === 'cancelled') {
        const reason = prompt('Please provide a reason for cancellation:');
        if (!reason) return;
        
        // Ask if service limit should be decreased
        const decreaseLimit = window.confirm(
          'Should the client service limit be decreased?\n\n' +
          'Click OK to decrease the service limit (late cancellation)\n' +
          'Click Cancel to NOT decrease the service limit (early cancellation)'
        );
        
        updateData.cancelReason = reason + (decreaseLimit ? ' (service limit decreased)' : ' (service limit NOT decreased)');
        updateData.decreaseServiceLimit = decreaseLimit;
      }
      
      await requestService.update(id, updateData);
      await loadRequests();
      if (detailsOpen && selectedRequest && selectedRequest.id === id) {
        const updated = requests.find(r => r.id === id);
        if (updated) {
          setSelectedRequest(updated);
          const attrs = updated.attributes || updated;
          setEditFormData({
            status: attrs.status || 'pending',
            description: attrs.description || '',
            notes: attrs.notes || ''
          });
        }
      }
      // Reload page after status change
      window.location.reload();
    } catch (error: any) {
      console.error('Error updating request status:', error);
      setError(error.response?.data?.error?.message || 'Failed to update status');
    }
  };

  const handleOpenChat = (request: Request, tab: 'customer' | 'provider' = 'customer') => {
    setSelectedRequest(request);
    setChatTab(tab);
    setChatOpen(true);
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setSelectedRequest(null);
    setMessages([]);
  };

  const handleSendMessage = async () => {
    if (!selectedRequest || !newMessage.trim()) return;

    let phoneNumber = '';
    
    if (chatTab === 'customer') {
      const client = selectedRequest.attributes?.insuranceClient?.data?.attributes || 
                    selectedRequest.attributes?.insuranceClient ||
                    selectedRequest.attributes?.customer?.data?.attributes ||
                    selectedRequest.attributes?.customer;
      phoneNumber = client?.phoneNumber || client?.phone || '';
    } else {
      const provider = selectedRequest.attributes?.serviceProvider?.data?.attributes || 
                      selectedRequest.attributes?.serviceProvider;
      phoneNumber = provider?.phone || '';
    }

    if (!phoneNumber) {
      setError('Phone number not found');
      return;
    }

    try {
      setSending(true);
      setError('');

      await api.post('/whatsapp-messages/send', {
        data: {
          phoneNumber: phoneNumber,
          message: newMessage,
          messageType: 'text',
          direction: 'outbound'
        }
      });

      setNewMessage('');
      setTimeout(loadMessages, 1000);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent any default behavior that might cause page reload
    event.preventDefault();
    event.stopPropagation();
    
    if (!selectedRequest || !event.target.files?.[0]) {
      console.log('[IMAGE UPLOAD] No file selected or no request selected');
      return;
    }

    let phoneNumber = '';
    
    if (chatTab === 'customer') {
      const client = selectedRequest.attributes?.insuranceClient?.data?.attributes || 
                    selectedRequest.attributes?.insuranceClient ||
                    selectedRequest.attributes?.customer?.data?.attributes ||
                    selectedRequest.attributes?.customer;
      phoneNumber = client?.phoneNumber || client?.phone || '';
    } else {
      const provider = selectedRequest.attributes?.serviceProvider?.data?.attributes || 
                      selectedRequest.attributes?.serviceProvider;
      phoneNumber = provider?.phone || '';
    }

    if (!phoneNumber) {
      console.error('[IMAGE UPLOAD] Phone number not found');
      setError('Phone number not found');
      return;
    }

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('files', file);

    // Store input element to reset it later
    const inputElement = event.target;

    try {
      setSending(true);
      setError('');

      console.log('[IMAGE UPLOAD] Starting upload...', { fileName: file.name, fileSize: file.size, fileType: file.type, phoneNumber });

      const uploadResponse = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('[IMAGE UPLOAD] Upload response:', uploadResponse.data);

      const imageUrl = uploadResponse.data[0]?.url;
      if (!imageUrl) {
        throw new Error('Failed to upload image');
      }

      console.log('[IMAGE UPLOAD] Image uploaded successfully, URL:', imageUrl);
      console.log('[IMAGE UPLOAD] Sending WhatsApp message...');

      await api.post('/whatsapp-messages/send', {
        data: {
          phoneNumber: phoneNumber,
          message: 'Image',
          messageType: 'image',
          mediaUrl: imageUrl,
          direction: 'outbound'
        }
      });

      console.log('[IMAGE UPLOAD] WhatsApp message sent successfully');
      setTimeout(loadMessages, 1000);
    } catch (err: any) {
      console.error('[IMAGE UPLOAD] Error sending image:', err);
      console.error('[IMAGE UPLOAD] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.error?.message || 'Failed to send image');
    } finally {
      setSending(false);
      // Reset input after a small delay
      setTimeout(() => {
        if (inputElement) inputElement.value = '';
        console.log('[IMAGE UPLOAD] Input reset, upload process complete');
      }, 100);
    }
  };

  const { paginatedCategories, allCategories } = categorizeRequests();
  const statusColumns = ['pending', 'assigned', 'active', 'invoice_sent', 'payment_received', 'completed', 'cancelled'];
  
  // Get column width based on status - completed and cancelled are much smaller (for fullscreen view)
  const getColumnWidth = (status: string): number => {
    if (status === 'completed' || status === 'cancelled') {
      return 180; // Smaller for completed and cancelled (count only)
    }
    return 260; // Width for other columns with cards
  };
  
  // Handle page change for a status
  const handlePageChange = (status: string, newPage: number) => {
    setStatusPages(prev => ({
      ...prev,
      [status]: newPage
    }));
  };
  
  // Get total pages for a status
  const getTotalPages = (status: string): number => {
    const total = allCategories[status]?.length || 0;
    return Math.ceil(total / ITEMS_PER_PAGE);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px - 48px)' }, // Account for header and MainLayout padding
      minHeight: 0, // Important for flex children to shrink
      overflow: 'hidden',
      px: { xs: 0.5, sm: 1, md: 2 },
      py: { xs: 1, sm: 1.5 },
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 }, 
        flexWrap: 'wrap', 
        gap: { xs: 1.5, sm: 2 },
        flexShrink: 0,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}
          >
            Automation Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Monitor and manage all service requests with real-time automation
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 2 }, 
          flexWrap: 'wrap',
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* Refresh Controls */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}>
            <IconButton
              onClick={handleManualRefresh}
              disabled={loading}
              size="small"
              title="Refresh Now"
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => handleToggleAutoRefresh(!autoRefreshEnabled)}
              size="small"
              title={autoRefreshEnabled ? "Pause Auto-Refresh" : "Resume Auto-Refresh"}
              sx={{ 
                color: autoRefreshEnabled ? 'success.main' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              {autoRefreshEnabled ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            {isRefreshing && (
              <CircularProgress size={16} sx={{ ml: 0.5 }} />
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setCreateModalOpen(true)}
            sx={{ 
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 }
            }}
            fullWidth={isMobile}
          >
            Create New Request
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={autoAssign}
                onChange={(e) => handleAutoAssignToggle(e.target.checked)}
                disabled={autoAssignLoading}
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label="Auto-Assign Requests"
            sx={{ 
              whiteSpace: 'nowrap',
              '& .MuiFormControlLabel-label': {
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
              }
            }}
          />
        </Box>
      </Box>

      {/* Alerts Section */}
      <Box sx={{ flexShrink: 0, mb: { xs: 1.5, sm: 2 } }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} 
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {autoAssign && (
          <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Auto-assign is enabled. New requests will be automatically assigned to available service providers based on their schedule.
          </Alert>
        )}
      </Box>

      {/* Tabs Section */}
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1, 
          borderColor: 'divider',
          flexShrink: 0,
          px: { xs: 1, sm: 2 },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ 
              flex: 1,
              minWidth: 0
            }}
          >
            <Tab label="Requests" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
            <Tab label="Map" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
          </Tabs>
          {activeTab === 0 && (
            <IconButton
              onClick={() => setKanbanFullscreen(true)}
              size="small"
              sx={{ ml: 1 }}
              title="Fullscreen Kanban"
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          )}
          {activeTab === 1 && (
            <IconButton
              onClick={() => setMapFullscreen(true)}
              size="small"
              sx={{ ml: 1 }}
              title="Fullscreen Map"
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Tab Panel for Columns */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 0}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: activeTab === 0 ? 'flex' : 'none',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', // 1 column on mobile (vertical stacking)
              sm: 'repeat(2, 1fr)', // 2 columns on tablet
              md: 'repeat(3, 1fr)', // 3 columns on medium screens
              lg: 'repeat(4, 1fr)', // 4 columns on large screens
              xl: 'repeat(5, 1fr)' // 5 columns on extra large (two rows)
            },
            gap: { xs: 2, sm: 2, md: 2.5 },
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            minHeight: 0,
            pb: { xs: 2, sm: 2 },
            pt: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            alignContent: 'start',
            '&::-webkit-scrollbar': {
              width: '12px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '6px',
              margin: '8px 0'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, #888 0%, #666 100%)',
              borderRadius: '6px',
              border: '2px solid #f1f1f1',
              '&:hover': {
                background: 'linear-gradient(180deg, #666 0%, #444 100%)',
              },
            },
          }}>
            {statusColumns.map((status) => {
              const columnWidth = getColumnWidth(status);
              return (
              <Box key={status} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: '400px', sm: '450px', md: '500px' },
                maxHeight: { xs: '600px', sm: '650px', md: '700px' },
                height: '100%',
                boxSizing: 'border-box'
              }}>
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: { xs: 1.5, md: 2 }, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: status === 'completed' ? '#e8f5e9' : status === 'cancelled' ? '#ffebee' : 'background.paper',
                    overflow: 'hidden',
                    width: '100%',
                    boxSizing: 'border-box',
                    border: status === 'completed' ? '2px solid #4caf50' : status === 'cancelled' ? '2px solid #f44336' : '1px solid',
                    borderColor: status === 'completed' || status === 'cancelled' ? undefined : 'divider',
                    borderRadius: 2,
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: 8
                    }
                  }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    flexShrink: 0,
                    fontWeight: 600
                  }}>
                    {getStatusLabel(status)} ({allCategories[status]?.length || 0})
                  </Typography>
                  
                  {/* Show only count for completed and cancelled */}
                  {(status === 'completed' || status === 'cancelled') ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      gap: 2
                    }}>
                      <Typography variant="h2" sx={{ fontWeight: 700, color: status === 'completed' ? 'success.main' : 'error.main' }}>
                        {allCategories[status]?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {status === 'completed' ? 'Completed Requests' : 'Cancelled Requests'}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Show paginated cards for other statuses */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        minHeight: 0,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#ccc',
                          borderRadius: '3px',
                          '&:hover': {
                            background: '#999',
                          },
                        },
                      }}>
                        {paginatedCategories[status]?.map(request => (
                          <RequestCard
                            key={request.id}
                            request={request}
                            onClick={() => handleOpenDetails(request)}
                            getStatusLabel={getStatusLabel}
                            getStatusColor={getStatusColor}
                          />
                        ))}
                      </Box>
                      
                      {/* Pagination controls */}
                      {getTotalPages(status) > 1 && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                          mx: -2,
                          px: 2,
                          pb: 1,
                          borderRadius: '0 0 8px 8px'
                        }}>
                          <Button
                            size="small"
                            onClick={() => handlePageChange(status, (statusPages[status] || 1) - 1)}
                            disabled={(statusPages[status] || 1) <= 1}
                            variant="contained"
                            sx={{ minWidth: 60 }}
                          >
                            ◄
                          </Button>
                          <Chip 
                            label={`${statusPages[status] || 1} / ${getTotalPages(status)}`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                          <Button
                            size="small"
                            onClick={() => handlePageChange(status, (statusPages[status] || 1) + 1)}
                            disabled={(statusPages[status] || 1) >= getTotalPages(status)}
                            variant="contained"
                            sx={{ minWidth: 60 }}
                          >
                            ►
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              </Box>
              );
            })}
          </Box>
        </Box>

        {/* Tab Panel for Map */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 1}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: activeTab === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            position: 'relative',
            mt: { xs: 1, sm: 2 }
          }}
        >
          <Paper sx={{ 
            flex: 1,
            minHeight: { xs: '400px', sm: '500px', md: '600px' },
            position: 'relative', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              borderBottom: 1, 
              borderColor: 'divider',
              flexShrink: 0
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Service Providers & In-Process Requests Map
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                View active service providers and requests that are in process (not completed or cancelled)
              </Typography>
            </Box>
            <Box sx={{ 
              flex: 1, 
              minHeight: { xs: '350px', sm: '450px', md: '550px' }, 
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              height: '100%'
            }}>
              {activeTab === 1 && (
                <AutomationMap
                  key={`map-${activeTab}-${serviceProviders.length}-${requests.length}`}
                  providers={serviceProviders}
                  requests={requests.filter(r => {
                    const status = r.attributes?.status;
                    return status !== 'completed' && status !== 'cancelled';
                  })}
                  onRequestClick={handleOpenDetails}
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Request Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          {/* Big Flag Banner */}
          {(() => {
            const attrs = selectedRequest?.attributes || {};
            const notes = attrs.notes || '';
            const description = attrs.description || '';
            const isNewUser = attrs.isNewUser || false;
            const isNotOurClient = attrs.isNotOurClient || false;
            const requiresPayment = attrs.requiresPayment || false;
            const isPolicyExpired = attrs.isPolicyExpired || notes.includes('POLICY EXPIRED') || description.includes('POLICY EXPIRED');
            const isExtraService = notes.includes('EXTRA SERVICE') || description.includes('EXTRA SERVICE') || 
                                   notes.includes('SERVICE LIMIT REACHED') || description.includes('SERVICE LIMIT REACHED');
            
            let flagType: 'not_our_client' | 'new_user' | 'extra_service' | 'policy_expired' | null = null;
            let flagMessage = '';
            let flagColor = '#f44336';
            
            if (isNotOurClient) {
              flagType = 'not_our_client';
              flagMessage = '🔴 NOT OUR CLIENT - VIN NOT FOUND';
              flagColor = '#d32f2f'; // Dark red
            } else if (isNewUser) {
              flagType = 'new_user';
              flagMessage = '🚩 NEW USER - NOT IN DATABASE';
              flagColor = '#f44336';
            } else if (isPolicyExpired) {
              flagType = 'policy_expired';
              flagMessage = '🔴 POLICY EXPIRED';
              flagColor = '#d32f2f'; // Dark red
            } else if (isExtraService || requiresPayment) {
              flagType = 'extra_service';
              flagMessage = '🚩 EXTRA SERVICE - NOT IN PACKAGE';
              flagColor = '#ff5722';
            }
            
            return flagType ? (
              <Box
                sx={{
                  backgroundColor: flagColor,
                  color: 'white',
                  py: 2,
                  px: 3,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  {flagMessage}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    opacity: 0.9,
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}
                >
                  {isNotOurClient && 'VIN number was provided but this client is NOT in our database. This is NOT our client. Sales team must contact customer before assignment.'}
                  {isNewUser && !isNotOurClient && 'This user is not registered in our database. Sales team should call.'}
                  {isPolicyExpired && 'This user\'s insurance policy has expired. Call center must contact customer before assignment.'}
                  {(isExtraService || requiresPayment) && !isNotOurClient && !isPolicyExpired && 'This service is not included in the client\'s package. Call center must contact customer before assignment.'}
                </Typography>
              </Box>
            ) : null;
          })()}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 3, pb: 2 }}>
            <Box>
              <Typography variant="h6">
                Request: {selectedRequest?.attributes?.requestNumber || `REQ-${selectedRequest?.id}`}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {!isEditing && (
                <IconButton onClick={() => setIsEditing(true)} color="primary">
                  <EditIcon />
                </IconButton>
              )}
              <IconButton onClick={handleCloseDetails}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={3}>
              {/* Workflow Stepper */}
              <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Workflow Progress
                </Typography>
                <Stepper 
                  activeStep={getStatusStep(selectedRequest.attributes?.status || 'pending')} 
                  alternativeLabel={!isMobile}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                    }
                  }}
                >
                  <Step><StepLabel>Pending</StepLabel></Step>
                  <Step><StepLabel>Assigned</StepLabel></Step>
                  <Step><StepLabel>Active</StepLabel></Step>
                  <Step><StepLabel>Invoice Sent</StepLabel></Step>
                  <Step><StepLabel>Payment Received</StepLabel></Step>
                  <Step><StepLabel>Completed</StepLabel></Step>
                </Stepper>
                <Alert severity={getStatusSeverity(selectedRequest.attributes?.status || 'pending')} sx={{ mt: 2 }}>
                  <strong>Current Status:</strong> {getStatusLabel(selectedRequest.attributes?.status || 'pending')}
                </Alert>
              </Paper>

              {/* Request Information */}
              <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Request Information
                </Typography>
                <Stack spacing={2}>
                  {/* Service Restriction Alert */}
                  {(() => {
                    const attrs = selectedRequest.attributes || {};
                    const notes = attrs.notes || '';
                    const description = attrs.description || '';
                    const isNewUser = attrs.isNewUser || false;
                    const requiresPayment = attrs.requiresPayment || false;
                    const isPolicyExpired = notes.includes('POLICY EXPIRED') || description.includes('POLICY EXPIRED');
                    const isExtraService = notes.includes('EXTRA SERVICE') || description.includes('EXTRA SERVICE') || 
                                           notes.includes('SERVICE LIMIT REACHED') || description.includes('SERVICE LIMIT REACHED');
                    
                    if (isNewUser || isPolicyExpired || isExtraService || requiresPayment) {
                      let alertMessage = '';
                      let alertSeverity: 'error' | 'warning' = 'error';
                      
                      if (isNewUser) {
                        alertMessage = '⚠️ NEW USER: This user is not registered in our database. Sales team should call before assignment.';
                        alertSeverity = 'error';
                      } else if (isPolicyExpired) {
                        alertMessage = '⚠️ POLICY EXPIRED: This user\'s insurance policy has expired. Call center must contact customer before assignment.';
                        alertSeverity = 'error';
                      } else if (isExtraService || requiresPayment) {
                        alertMessage = '⚠️ EXTRA SERVICE: This service is not included in the client\'s package. Call center must contact customer before assignment.';
                        alertSeverity = 'warning';
                      }
                      
                      return (
                        <Alert severity={alertSeverity} sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', md: '1rem' } }}>
                          {alertMessage}
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                  {isEditing ? (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={editFormData.status}
                          label="Status"
                          onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="assigned">Assigned</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="invoice_creation">Invoice Creation</MenuItem>
                          <MenuItem value="invoice_sent">Invoice Sent</MenuItem>
                          <MenuItem value="payment_received">Payment Received</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={3}
                        value={editFormData.notes || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      />
                      
                      {/* Location Map */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                          Location (Click on map to update)
                        </Typography>
                        
                        {/* Map Picker */}
                        <Box sx={{ height: 400, width: '100%', mb: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                          <MapContainer
                            center={editMapCenter}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapClickHandler
                              onLocationSelect={(lat, lng) => {
                                setEditLocationLatitude(lat);
                                setEditLocationLongitude(lng);
                                setEditMapCenter([lat, lng]);
                              }}
                            />
                            {editLocationLatitude !== null && editLocationLongitude !== null && (
                              <Marker position={[editLocationLatitude, editLocationLongitude]} />
                            )}
                          </MapContainer>
                        </Box>

                        {/* Display selected coordinates */}
                        {editLocationLatitude !== null && editLocationLongitude !== null && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Selected: {editLocationLatitude.toFixed(6)}, {editLocationLongitude.toFixed(6)}
                          </Typography>
                        )}

                        {/* Button to get current location */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            if (navigator.geolocation) {
                              navigator.geolocation.getCurrentPosition(
                                (position) => {
                                  const lat = position.coords.latitude;
                                  const lng = position.coords.longitude;
                                  setEditLocationLatitude(lat);
                                  setEditLocationLongitude(lng);
                                  setEditMapCenter([lat, lng]);
                                },
                                (error) => {
                                  console.error('Error getting location:', error);
                                  setError('Unable to get your current location');
                                }
                              );
                            } else {
                              setError('Geolocation is not supported by your browser');
                            }
                          }}
                          sx={{ mt: 1 }}
                        >
                          Use My Current Location
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={getStatusLabel(selectedRequest.attributes?.status || 'pending')} 
                            color={getStatusColor(selectedRequest.attributes?.status || 'pending')} 
                            size="small" 
                          />
                        </Box>
                      </Box>
                      {selectedRequest.attributes?.phoneNumber && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Request Phone Number
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            📱 {selectedRequest.attributes.phoneNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            Phone number from which this request was created (may differ from client number)
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary">Description</Typography>
                        <Typography variant="body1" component="span" sx={{ mt: 0.5, display: 'block' }}>
                          {descriptionWithMapLink(selectedRequest.attributes?.description) ?? 'N/A'}
                        </Typography>
                      </Box>
                      {selectedRequest.attributes?.notes && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Notes</Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {selectedRequest.attributes.notes}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Cancel Reason - Show if request is cancelled */}
                      {selectedRequest.attributes?.status === 'cancelled' && selectedRequest.attributes?.cancelReason && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            ❌ Cancellation Reason
                          </Typography>
                          <Box sx={{ mt: 1, p: 2, bgcolor: 'error.50', border: '2px solid', borderColor: 'error.main', borderRadius: 1 }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'error.dark', fontWeight: 500 }}>
                              {selectedRequest.attributes.cancelReason}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Provider Change Reason - Show if provider was changed */}
                      {selectedRequest.attributes?.providerChangeReason && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            🔄 Provider Change Reason
                          </Typography>
                          <Box sx={{ mt: 1, p: 2, bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'warning.dark', fontWeight: 500 }}>
                              {selectedRequest.attributes.providerChangeReason}
                            </Typography>
                            {selectedRequest.attributes.previousServiceProvider && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Previous Provider: {
                                  selectedRequest.attributes.previousServiceProvider.data?.attributes?.name ||
                                  selectedRequest.attributes.previousServiceProvider.name ||
                                  'N/A'
                                }
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      {selectedRequest.attributes?.serviceType && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Service Type</Typography>
                          <Chip label={selectedRequest.attributes.serviceType} size="small" sx={{ mt: 0.5 }} />
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Location
                        </Typography>
                        {selectedRequest.attributes?.locationText && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {selectedRequest.attributes.locationText}
                          </Typography>
                        )}
                        <Box sx={{ height: 300, width: '100%', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', mt: 1 }}>
                          <MapContainer
                            center={
                              selectedRequest.attributes?.locationLatitude && selectedRequest.attributes?.locationLongitude
                                ? [selectedRequest.attributes.locationLatitude, selectedRequest.attributes.locationLongitude]
                                : [23.5859, 58.4059] // Default to Muscat, Oman
                            }
                            zoom={selectedRequest.attributes?.locationLatitude && selectedRequest.attributes?.locationLongitude ? 13 : 10}
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {selectedRequest.attributes?.locationLatitude && selectedRequest.attributes?.locationLongitude && (
                              <Marker position={[selectedRequest.attributes.locationLatitude, selectedRequest.attributes.locationLongitude]} />
                            )}
                          </MapContainer>
                        </Box>
                        {selectedRequest.attributes?.locationLatitude && selectedRequest.attributes?.locationLongitude ? (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            <Link
                              href={`https://www.google.com/maps?q=${selectedRequest.attributes.locationLatitude},${selectedRequest.attributes.locationLongitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View on Google Maps
                            </Link>
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            No location data available
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Stack>
              </Paper>

              {/* Invoice Information - always visible */}
              <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Invoice Information
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenInvoicesModal}
                    sx={{ minWidth: 150 }}
                  >
                    View All Invoices
                  </Button>
                </Box>
                {(() => {
                  const requestAttrs = selectedRequest.attributes ?? selectedRequest;
                  const invoices = requestAttrs.invoices?.data ?? requestAttrs.invoices ?? [];
                  const customerInvoice = invoices.find((inv: any) => {
                    const invAttrs = inv.attributes ?? inv;
                    return invAttrs.invoiceType === 'service_provider_to_darb';
                  });
                  const insuranceInvoice = invoices.find((inv: any) => {
                    const invAttrs = inv.attributes ?? inv;
                    return invAttrs.invoiceType === 'insurance_company_monthly';
                  });
                  const legacyInvoice = selectedRequest.attributes?.invoice?.data?.attributes ?? selectedRequest.attributes?.invoice?.data ?? selectedRequest.attributes?.invoice;
                  const customerInvoiceData = customerInvoice?.attributes ?? customerInvoice;
                  const insuranceInvoiceData = insuranceInvoice?.attributes ?? insuranceInvoice;
                  const hasAny = customerInvoiceData || insuranceInvoiceData || legacyInvoice;
                  if (!hasAny) {
                    return (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No invoices yet. Create an invoice or open &quot;View All Invoices&quot; for more options.
                      </Typography>
                    );
                  }
                  return (
                    <Stack spacing={3}>
                      {/* Customer Invoice */}
                      {customerInvoiceData && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                            Customer Invoice
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Invoice Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                {customerInvoiceData.invoiceNumber ?? '-'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Total Amount
                              </Typography>
                              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                OMR {((customerInvoiceData.total ?? 0)).toFixed(3)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Items
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={() => handleAddInvoiceItem('customer')}
                                sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                              >
                                Add Item
                              </Button>
                            </Box>
                            {customerInvoiceItems.length > 0 ? (
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {customerInvoiceItems.map((item: any, idx: number) => {
                                  const itemAttrs = item.attributes ?? item;
                                  return (
                                    <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                          {itemAttrs.description ?? '-'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                          Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '70px', textAlign: 'right' }}>
                                          OMR {(itemAttrs.total ?? 0).toFixed(3)}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'customer')} sx={{ p: 0.5 }}>
                                          <Edit sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'customer')} sx={{ p: 0.5 }} color="error">
                                          <Delete sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No items yet. Click "Add Item" to add one.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      {/* Insurance Invoice */}
                      {insuranceInvoiceData && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                            Insurance Invoice
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Invoice Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                {insuranceInvoiceData.invoiceNumber ?? '-'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Total Amount
                              </Typography>
                              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                OMR {((insuranceInvoiceData.total ?? 0)).toFixed(3)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Items
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={() => handleAddInvoiceItem('insurance')}
                                sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                              >
                                Add Item
                              </Button>
                            </Box>
                            {insuranceInvoiceItems.length > 0 ? (
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {insuranceInvoiceItems.map((item: any, idx: number) => {
                                  const itemAttrs = item.attributes ?? item;
                                  return (
                                    <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                          {itemAttrs.description ?? '-'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                          Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '70px', textAlign: 'right' }}>
                                          OMR {(itemAttrs.total ?? 0).toFixed(3)}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'insurance')} sx={{ p: 0.5 }}>
                                          <Edit sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'insurance')} sx={{ p: 0.5 }} color="error">
                                          <Delete sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No items yet. Click "Add Item" to add one.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      {/* Legacy single invoice fallback - with Edit/Add/Delete */}
                      {!customerInvoiceData && !insuranceInvoiceData && legacyInvoice && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                            Invoice
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Invoice Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                {legacyInvoice.invoiceNumber ?? '-'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                Total Amount
                              </Typography>
                              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                OMR {((legacyInvoice.total ?? 0)).toFixed(3)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Items
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={() => handleAddInvoiceItem('customer')}
                                sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                              >
                                Add Item
                              </Button>
                            </Box>
                            {customerInvoiceItems.length > 0 ? (
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {customerInvoiceItems.map((item: any, idx: number) => {
                                  const itemAttrs = item.attributes ?? item;
                                  return (
                                    <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                          {itemAttrs.description ?? '-'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                          Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '70px', textAlign: 'right' }}>
                                          OMR {(itemAttrs.total ?? 0).toFixed(3)}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'customer')} sx={{ p: 0.5 }}>
                                          <Edit sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'customer')} sx={{ p: 0.5 }} color="error">
                                          <Delete sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No items yet. Click &quot;Add Item&quot; to add one.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<PdfIcon />}
                          onClick={() => handleDownloadInvoicePDF('customer')}
                          sx={{ minWidth: 180 }}
                          disabled={!customerInvoiceData}
                        >
                          Customer Invoice
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PdfIcon />}
                          onClick={() => handleDownloadInvoicePDF('insurance')}
                          sx={{ minWidth: 180 }}
                          disabled={!insuranceInvoiceData}
                        >
                          Insurance Invoice
                        </Button>
                      </Box>
                    </Stack>
                  );
                })()}
              </Paper>

              {/* Before/After Photos */}
              <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Evidence Photos
                </Typography>
                {isEditing ? (
                  <Stack spacing={2}>
                    {/* Before Photo Upload */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Before Photo
                      </Typography>
                      {(() => {
                        const attrs = selectedRequest?.attributes || selectedRequest;
                        const href = getRequestLocationHref(attrs);
                        return href ? (
                          <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ display: 'block', mb: 1, fontSize: '0.875rem' }}
                          >
                            Location link
                          </Link>
                        ) : null;
                      })()}
                      {beforePhotoPreview ? (
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                          <img 
                            src={beforePhotoPreview} 
                            alt="Before" 
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                          />
                          <IconButton
                            size="small"
                            onClick={removeBeforePhoto}
                            sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : null}
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="before-photo-upload-automation"
                        type="file"
                        onChange={handleBeforePhotoChange}
                      />
                      <label htmlFor="before-photo-upload-automation">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          {beforePhotoPreview ? 'Change Before Photo' : 'Upload Before Photo'}
                        </Button>
                      </label>
                    </Box>

                    {/* After Photo Upload */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        After Photo
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={TO_DROP_DOWN_OPTIONS}
                        value={
                          editFormData?.toDropDown && TO_DROP_DOWN_OPTIONS.includes(editFormData.toDropDown)
                            ? editFormData.toDropDown
                            : null
                        }
                        onChange={(_e, newValue) => {
                          setEditFormData((prev: any) => ({ ...prev, toDropDown: newValue || null }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="To Drop Down"
                            placeholder="Select..."
                            sx={{ mb: 1, maxWidth: 420 }}
                          />
                        )}
                      />
                      {afterPhotoPreview ? (
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                          <img 
                            src={afterPhotoPreview} 
                            alt="After" 
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                          />
                          <IconButton
                            size="small"
                            onClick={removeAfterPhoto}
                            sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : null}
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="after-photo-upload-automation"
                        type="file"
                        onChange={handleAfterPhotoChange}
                      />
                      <label htmlFor="after-photo-upload-automation">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          fullWidth
                        >
                          {afterPhotoPreview ? 'Change After Photo' : 'Upload After Photo'}
                        </Button>
                      </label>
                    </Box>
                  </Stack>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Before
                      </Typography>
                      {(() => {
                        const attrs = selectedRequest?.attributes || selectedRequest;
                        const href = getRequestLocationHref(attrs);
                        return href ? (
                          <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}
                          >
                            Location link
                          </Link>
                        ) : null;
                      })()}
                      {(() => {
                        const beforePhoto = selectedRequest.attributes?.evidencePhotoBefore?.data || selectedRequest.attributes?.evidencePhotoBefore;
                        const imageUrl = getImageUrl(beforePhoto);
                        const hasImage = hasValidImage(beforePhoto) && !imageErrors.before && imageUrl;
                        
                        return hasImage ? (
                          <Box>
                            <img
                              src={imageUrl}
                              alt="Before"
                              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                              onError={() => setImageErrors(prev => ({ ...prev, before: true }))}
                            />
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="admin-before-photo-upload"
                              type="file"
                              onChange={handleAdminBeforePhotoUpload}
                              disabled={uploading}
                            />
                            <label htmlFor="admin-before-photo-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                size="small"
                                startIcon={<CloudUpload />}
                                sx={{ mt: 1, width: '100%' }}
                                disabled={uploading}
                                onClick={(e) => e.preventDefault()}
                              >
                                {uploading ? 'Uploading...' : 'Upload/Change'}
                              </Button>
                            </label>
                          </Box>
                        ) : (
                          <Box>
                            <Box
                              sx={{
                                width: '300px',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: 'text.secondary'
                              }}
                            >
                              <Typography variant="body2">No image</Typography>
                            </Box>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="admin-before-photo-upload"
                              type="file"
                              onChange={handleAdminBeforePhotoUpload}
                              disabled={uploading}
                            />
                            <label htmlFor="admin-before-photo-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                size="small"
                                startIcon={<CloudUpload />}
                                sx={{ mt: 1, width: '100%' }}
                                disabled={uploading}
                              >
                                {uploading ? 'Uploading...' : 'Upload Photo'}
                              </Button>
                            </label>
                          </Box>
                        );
                      })()}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        After
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={TO_DROP_DOWN_OPTIONS}
                        value={(() => {
                          const attrs = selectedRequest?.attributes || selectedRequest;
                          const current = attrs?.toDropDown ?? null;
                          return current && TO_DROP_DOWN_OPTIONS.includes(current) ? current : null;
                        })()}
                        onChange={async (_e, newValue) => {
                          if (!selectedRequest) return;
                          try {
                            await requestService.update(selectedRequest.id, { toDropDown: newValue || null } as any);
                            await reloadRequestDetails(selectedRequest.id);
                          } catch (err: any) {
                            console.error('Failed to update toDropDown:', err);
                            setError(err?.response?.data?.error?.message || 'Failed to update To Drop Down');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="To Drop Down"
                            placeholder="Select..."
                            sx={{ mb: 1, minWidth: 300 }}
                          />
                        )}
                      />
                      {(() => {
                        const afterPhoto = selectedRequest.attributes?.evidencePhotoAfter?.data || selectedRequest.attributes?.evidencePhotoAfter;
                        const imageUrl = getImageUrl(afterPhoto);
                        const hasImage = hasValidImage(afterPhoto) && !imageErrors.after && imageUrl;
                        
                        return hasImage ? (
                          <Box>
                            <img
                              src={imageUrl}
                              alt="After"
                              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                              onError={() => setImageErrors(prev => ({ ...prev, after: true }))}
                            />
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="admin-after-photo-upload"
                              type="file"
                              onChange={handleAdminAfterPhotoUpload}
                              disabled={uploading}
                            />
                            <label htmlFor="admin-after-photo-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                size="small"
                                startIcon={<CloudUpload />}
                                sx={{ mt: 1, width: '100%' }}
                                disabled={uploading}
                              >
                                {uploading ? 'Uploading...' : 'Upload/Change'}
                              </Button>
                            </label>
                          </Box>
                        ) : (
                          <Box>
                            <Box
                              sx={{
                                width: '300px',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: 'text.secondary'
                              }}
                            >
                              <Typography variant="body2">No image</Typography>
                            </Box>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id="admin-after-photo-upload"
                              type="file"
                              onChange={handleAdminAfterPhotoUpload}
                              disabled={uploading}
                            />
                            <label htmlFor="admin-after-photo-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                size="small"
                                startIcon={<CloudUpload />}
                                sx={{ mt: 1, width: '100%' }}
                                disabled={uploading}
                                onClick={(e) => e.preventDefault()}
                              >
                                {uploading ? 'Uploading...' : 'Upload Photo'}
                              </Button>
                            </label>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Client Information */}
              {(selectedRequest.attributes?.insuranceClient || selectedRequest.attributes?.customer) && (
                <Paper sx={{ p: { xs: 2, md: 3 }, border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' }, color: 'primary.main' }}>
                    👤 Client Information
                  </Typography>
                  <Stack spacing={2}>
                    {(() => {
                      const insuranceClient = selectedRequest.attributes?.insuranceClient?.data?.attributes || selectedRequest.attributes?.insuranceClient?.data || selectedRequest.attributes?.insuranceClient;
                      const customer = selectedRequest.attributes?.customer?.data?.attributes || selectedRequest.attributes?.customer?.data || selectedRequest.attributes?.customer;
                      const clientId = selectedRequest.attributes?.insuranceClient?.data?.id || selectedRequest.attributes?.insuranceClient?.id;
                      const customerId = selectedRequest.attributes?.customer?.data?.id || selectedRequest.attributes?.customer?.id;
                      
                      // Insurance Client
                      if (insuranceClient) {
                        return (
                          <>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Client Type
                              </Typography>
                              <Chip label="Insurance Client" color="primary" size="small" sx={{ mt: 0.5 }} />
                            </Box>
                            
                            {insuranceClient.customerName && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Customer Name
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                                  {clientId ? (
                                    <Link
                                      component="button"
                                      variant="body1"
                                      onClick={() => {
                                        handleCloseDetails();
                                        navigate(`/insurance-clients/${clientId}/overview`);
                                      }}
                                      sx={{ 
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {insuranceClient.customerName} →
                                    </Link>
                                  ) : insuranceClient.customerName}
                                </Typography>
                              </Box>
                            )}
                            
                            {insuranceClient.vinNumber && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  VIN Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {clientId ? (
                                    <Link
                                      component="button"
                                      variant="body1"
                                      onClick={() => {
                                        handleCloseDetails();
                                        navigate(`/insurance-clients/${clientId}/overview`);
                                      }}
                                      sx={{ 
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {insuranceClient.vinNumber} →
                                    </Link>
                                  ) : (
                                    <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                      {insuranceClient.vinNumber}
                                    </span>
                                  )}
                                </Typography>
                              </Box>
                            )}
                            
                            {insuranceClient.phoneNumber && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Phone Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {insuranceClient.phoneNumber}
                                </Typography>
                              </Box>
                            )}
                            
                            {insuranceClient.email && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Email
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {insuranceClient.email}
                                </Typography>
                              </Box>
                            )}
                            
                            {(insuranceClient.plateNumber || insuranceClient.carMake || insuranceClient.carModel) && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Vehicle Details
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {[insuranceClient.plateNumber, insuranceClient.carMake, insuranceClient.carModel, insuranceClient.carYear].filter(Boolean).join(' • ')}
                                </Typography>
                              </Box>
                            )}
                          </>
                        );
                      }
                      
                      // Regular Customer
                      if (customer) {
                        return (
                          <>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Client Type
                              </Typography>
                              <Chip label="Regular Customer" color="secondary" size="small" sx={{ mt: 0.5 }} />
                            </Box>
                            
                            {(customer.contactPerson || customer.companyName) && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Customer Name
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                                  {customerId ? (
                                    <Link
                                      component="button"
                                      variant="body1"
                                      onClick={() => {
                                        handleCloseDetails();
                                        navigate(`/customers/${customerId}/overview`);
                                      }}
                                      sx={{ 
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        color: 'secondary.main',
                                        fontWeight: 600,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {customer.contactPerson || customer.companyName} →
                                    </Link>
                                  ) : (customer.contactPerson || customer.companyName)}
                                </Typography>
                              </Box>
                            )}
                            
                            {customer.customerNumber && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Customer Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace', fontWeight: 500 }}>
                                  {customer.customerNumber}
                                </Typography>
                              </Box>
                            )}
                            
                            {(customer.phone || customer.mobile) && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Phone Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {customer.phone || customer.mobile}
                                </Typography>
                              </Box>
                            )}
                            
                            {customer.email && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Email
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {customer.email}
                                </Typography>
                              </Box>
                            )}
                          </>
                        );
                      }
                      
                      return (
                        <Typography variant="body2" color="text.secondary">
                          No client information available
                        </Typography>
                      );
                    })()}
                  </Stack>
                </Paper>
              )}

              {/* Service Provider Information */}
              {selectedRequest.attributes?.serviceProvider && (
                <Paper sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Service Provider
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Provider Name</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedRequest.attributes.serviceProvider?.data?.attributes?.name ? (
                          <Link
                            component="button"
                            variant="body1"
                            onClick={() => {
                              const providerId = selectedRequest.attributes?.serviceProvider?.data?.id;
                              if (providerId) {
                                navigate(`/service-providers/${providerId}/overview`);
                              }
                            }}
                            sx={{ 
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {selectedRequest.attributes.serviceProvider?.data?.attributes?.name}
                          </Link>
                        ) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedRequest.attributes.serviceProvider?.data?.attributes?.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {/* Chat Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexDirection: { xs: 'column', sm: 'row' } 
              }}>
                {(selectedRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 
                  selectedRequest.attributes?.customer?.data?.attributes?.phone) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      const phone = selectedRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 
                                   selectedRequest.attributes?.customer?.data?.attributes?.phone;
                      if (phone) {
                        openWhatsAppChat(phone);
                      }
                    }}
                    fullWidth
                    sx={{ 
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#20BA5A' }
                    }}
                  >
                    Chat with Client
                  </Button>
                )}
                {selectedRequest.attributes?.serviceProvider?.data?.attributes?.phone && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      const phone = selectedRequest.attributes?.serviceProvider?.data?.attributes?.phone;
                      if (phone) {
                        openWhatsAppChat(phone);
                      }
                    }}
                    fullWidth
                    sx={{ 
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#20BA5A' }
                    }}
                  >
                    Chat with Service Provider
                  </Button>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button onClick={handleCloseDetails} color="inherit">
            Close
          </Button>
          {!isEditing && selectedRequest && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                onClick={() => setIsEditing(true)}
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
              >
                Edit Request
              </Button>
              {/* Assign Client buttons for NOT OUR CLIENT requests */}
              {(() => {
                const attrs = selectedRequest.attributes || {};
                const isNotOurClient = attrs.isNotOurClient || false;
                if (isNotOurClient) {
                  return (
                    <>
                      <Button
                        onClick={() => handleAssignClientOpen(selectedRequest)}
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                      >
                        Create New Insurance Client
                      </Button>
                      <Button
                        onClick={() => handleAssignClientOpen(selectedRequest)}
                        variant="outlined"
                        color="primary"
                        startIcon={<SwapHoriz />}
                      >
                        Assign Client
                      </Button>
                    </>
                  );
                }
                return null;
              })()}
              {/* Assign button for pending requests */}
              {(selectedRequest.attributes?.status === 'pending') && 
               !selectedRequest.attributes?.serviceProvider?.data && (
                <Button
                  onClick={() => handleAssignDialogOpen(selectedRequest)}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Assign Service Provider
                </Button>
              )}
              
              {/* Workflow progression buttons */}
              {(selectedRequest.attributes?.status === 'assigned') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(selectedRequest.id, 'active');
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Start Work (Mark Active)
                </Button>
              )}
              {(selectedRequest.attributes?.status === 'active') && (
                <Button
                  onClick={() => {
                    setRequestForInvoice(selectedRequest);
                    setInvoiceModalOpen(true);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Send Invoice
                </Button>
              )}
              {(selectedRequest.attributes?.status === 'invoice_sent') && (
                (Number(selectedRequest.attributes?.extraKilometers) === 0) ? (
                  <Button
                    onClick={async () => {
                      try {
                        await requestService.update(selectedRequest.id, {
                          status: 'payment_received',
                          paymentStatus: 'paid',
                          paymentReceivedAt: new Date().toISOString(),
                          completedDate: new Date().toISOString()
                        } as any);
                        await requestService.update(selectedRequest.id, {
                          status: 'completed'
                        } as any);
                        await loadRequests();
                      } catch (err: any) {
                        setError(err.response?.data?.error?.message || 'Failed to complete request');
                      }
                      handleCloseDetails();
                    }}
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                  >
                    Complete (No Payment Needed)
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setRequestForPayment(selectedRequest);
                      setPaymentModalOpen(true);
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                  >
                    Confirm Payment
                  </Button>
                )
              )}
              {(selectedRequest.attributes?.status === 'payment_received') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(selectedRequest.id, 'completed');
                    handleCloseDetails();
                  }}
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                >
                  Complete Request
                </Button>
              )}

              {/* Change provider button for active requests */}
              {(selectedRequest.attributes?.status !== 'pending' && 
                selectedRequest.attributes?.status !== 'completed' && 
                selectedRequest.attributes?.status !== 'cancelled') && (
                <Button
                  onClick={() => handleChangeProviderOpen(selectedRequest)}
                  variant="outlined"
                  color="warning"
                  startIcon={<SwapHoriz />}
                >
                  Change Provider
                </Button>
              )}
              
              {/* Cancel button for non-completed requests */}
              {(selectedRequest.attributes?.status !== 'completed' && 
                selectedRequest.attributes?.status !== 'cancelled') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(selectedRequest.id, 'cancelled');
                    handleCloseDetails();
                  }}
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                >
                  Cancel Request
                </Button>
              )}
            </Box>
          )}
          {isEditing && (
            <>
              <Button onClick={() => setIsEditing(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} variant="contained" disabled={uploading} startIcon={<Save />}>
                {uploading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      <Dialog
        open={chatOpen}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {chatTab === 'customer' ? 'Customer' : 'Service Provider'} Chat
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Request: {selectedRequest?.attributes?.requestNumber || `REQ-${selectedRequest?.id}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseChat}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={chatTab} onChange={(e, v) => setChatTab(v)} sx={{ mb: 2 }}>
            {(selectedRequest?.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 
              selectedRequest?.attributes?.customer?.data?.attributes?.phone) && (
              <Tab label="Customer" value="customer" />
            )}
            {selectedRequest?.attributes?.serviceProvider?.data?.attributes?.phone && (
              <Tab label="Service Provider" value="provider" />
            )}
          </Tabs>

          {/* Messages */}
          <Box sx={{ 
            maxHeight: { xs: '300px', sm: '400px' }, 
            overflowY: 'auto', 
            mb: 2, 
            p: { xs: 0.5, md: 1 }, 
            bgcolor: '#f5f5f5', 
            borderRadius: 1 
          }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Paper
                  sx={{
                    p: { xs: 1, md: 1.5 },
                    maxWidth: { xs: '85%', sm: '75%', md: '70%' },
                    bgcolor: msg.direction === 'outbound' ? '#dcf8c6' : '#ffffff',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    {msg.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                    {new Date(msg.sentAt).toLocaleString()}
                  </Typography>
                  {msg.mediaUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img src={msg.mediaUrl} alt="Media" style={{ maxWidth: '100%', borderRadius: 4 }} />
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <IconButton component="span" disabled={sending}>
                <ImageIcon />
              </IconButton>
            </label>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={sending}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Assign Provider Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {assigningRequest?.attributes?.requestNumber || `REQ-${assigningRequest?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assigningRequest?.attributes?.description || assigningRequest?.description}
              </Typography>
            </Box>
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={providerToAssign}
              onChange={(_, newValue) => setProviderToAssign(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                const location = attrs.area || attrs.address || 'No location';
                const schedule = formatSchedule(attrs.schedule);
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        📍 {location}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        🕐 {schedule}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button onClick={handleAssignProvider} variant="contained" color="success">
            Assign Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Provider Dialog */}
      <Dialog open={changeProviderOpen} onClose={handleChangeProviderClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {changingRequest?.attributes?.requestNumber || `REQ-${changingRequest?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {changingRequest?.attributes?.description || changingRequest?.description}
              </Typography>
            </Box>
            {changingRequest?.attributes?.serviceProvider?.data?.attributes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Current Provider</Typography>
                <Typography variant="body1">
                  {changingRequest.attributes.serviceProvider.data.attributes.name}
                </Typography>
              </Box>
            )}
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={newProvider}
              onChange={(_, newValue) => setNewProvider(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select New Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                const location = attrs.area || attrs.address || 'No location';
                const schedule = formatSchedule(attrs.schedule);
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        📍 {location}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        🕐 {schedule}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangeProviderClose}>Cancel</Button>
          <Button onClick={handleChangeProvider} variant="contained" color="primary">
            Change Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Client Modal */}
      <Dialog open={assignClientModalOpen} onClose={handleAssignClientClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {creatingNewClient ? 'Create New Insurance Client & Assign to Request' : 'Assign Client to Request'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {assigningRequestForClient?.attributes?.requestNumber || `REQ-${assigningRequestForClient?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assigningRequestForClient?.attributes?.description || assigningRequestForClient?.description}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={!creatingNewClient ? 'contained' : 'outlined'}
                onClick={() => setCreatingNewClient(false)}
                fullWidth
              >
                Assign Existing Client
              </Button>
              <Button
                variant={creatingNewClient ? 'contained' : 'outlined'}
                onClick={() => setCreatingNewClient(true)}
                fullWidth
              >
                Create New Client
              </Button>
            </Box>

            {!creatingNewClient ? (
              // Assign Existing Client
              <Autocomplete
                fullWidth
                options={insuranceClients}
                value={selectedClientToAssign}
                onChange={(_, newValue) => setSelectedClientToAssign(newValue)}
                getOptionLabel={(option) => {
                  const attrs = option.attributes || option;
                  const name = attrs.customerName || '';
                  const vin = attrs.vinNumber || '';
                  return name && vin ? `${name} (${vin})` : name || vin || `Client ${option.id}`;
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchLower = inputValue.toLowerCase();
                  return options.filter((option) => {
                    const attrs = option.attributes || option;
                    const name = (attrs.customerName || '').toLowerCase();
                    const vin = (attrs.vinNumber || '').toLowerCase();
                    const phone = (attrs.phoneNumber || '').toLowerCase();
                    return name.includes(searchLower) || vin.includes(searchLower) || phone.includes(searchLower);
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Insurance Client *"
                    placeholder="Search by name, VIN, or phone..."
                    required
                  />
                )}
                renderOption={(props, option) => {
                  const attrs = option.attributes || option;
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body1" fontWeight={600}>
                          {attrs.customerName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          VIN: {attrs.vinNumber || 'N/A'}
                        </Typography>
                        {attrs.phoneNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone: {attrs.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                noOptionsText="No clients found"
              />
            ) : (
              // Create New Client Form
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="VIN Number *"
                  value={newClientFormData.vinNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, vinNumber: e.target.value.toUpperCase() })}
                  required
                  inputProps={{ maxLength: 17 }}
                />
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={newClientFormData.customerName}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, customerName: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newClientFormData.phoneNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, phoneNumber: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newClientFormData.email}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, email: e.target.value })}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Plate Number"
                    value={newClientFormData.plateNumber}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, plateNumber: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Make"
                    value={newClientFormData.carMake}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carMake: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Model"
                    value={newClientFormData.carModel}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carModel: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Year"
                    value={newClientFormData.carYear}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carYear: e.target.value })}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={newClientFormData.policyNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, policyNumber: e.target.value })}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Policy Start Date"
                    type="date"
                    value={newClientFormData.policyStartDate}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, policyStartDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="Policy End Date"
                    type="date"
                    value={newClientFormData.policyEndDate}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, policyEndDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Autocomplete
                  fullWidth
                  options={insuranceCompanies}
                  value={insuranceCompanies.find(c => {
                    const id = c.id || c.attributes?.id || c.data?.id;
                    return id?.toString() === newClientFormData.insuranceCompany?.toString();
                  }) || null}
                  onChange={(_, newValue) => {
                    const id = newValue?.id || newValue?.attributes?.id || newValue?.data?.id;
                    setNewClientFormData({ ...newClientFormData, insuranceCompany: id || '' });
                  }}
                  getOptionLabel={(option) => {
                    const attrs = option.attributes || option.data?.attributes || option;
                    return attrs.name || `Company ${option.id}`;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Insurance Company" placeholder="Select company..." />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={servicePackages}
                  value={servicePackages.find(p => {
                    const id = p.id || p.attributes?.id || p.data?.id;
                    return id?.toString() === newClientFormData.servicePackage?.toString();
                  }) || null}
                  onChange={(_, newValue) => {
                    const id = newValue?.id || newValue?.attributes?.id || newValue?.data?.id;
                    setNewClientFormData({ ...newClientFormData, servicePackage: id || '' });
                  }}
                  getOptionLabel={(option) => {
                    const attrs = option.attributes || option.data?.attributes || option;
                    return attrs.name || `Package ${option.id}`;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Service Package" placeholder="Select package..." />
                  )}
                />
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={newClientFormData.notes}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, notes: e.target.value })}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignClientClose} disabled={assigningClient}>
            Cancel
          </Button>
          {!creatingNewClient ? (
            <Button
              onClick={handleAssignExistingClient}
              variant="contained"
              color="primary"
              disabled={!selectedClientToAssign || assigningClient}
            >
              {assigningClient ? 'Assigning...' : 'Assign Client'}
            </Button>
          ) : (
            <Button
              onClick={handleCreateAndAssignClient}
              variant="contained"
              color="primary"
              disabled={!newClientFormData.vinNumber || !newClientFormData.customerName || assigningClient}
            >
              {assigningClient ? 'Creating...' : 'Create & Assign Client'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Invoice Modal */}
      {requestForInvoice && (
        <CreateInvoiceModal
          open={invoiceModalOpen}
          onClose={() => {
            setInvoiceModalOpen(false);
            setRequestForInvoice(null);
          }}
          request={requestForInvoice}
          onSuccess={async () => {
            await loadRequests();
            // Reload the selected request if it's the same one
            if (requestForInvoice) {
              await reloadRequestDetails(requestForInvoice.id);
            }
            // Keep the modal open to show the invoice
          }}
        />
      )}

      {/* Add Payment Modal */}
      {requestForPayment && (
        <AddPaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setRequestForPayment(null);
          }}
          request={requestForPayment}
          onSuccess={() => {
            loadRequests();
            handleCloseDetails();
            // Reload page after payment recording and status change
            window.location.reload();
          }}
        />
      )}

      {/* All Invoices Modal */}
      <Dialog 
        open={invoicesModalOpen} 
        onClose={() => setInvoicesModalOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">All Invoices</Typography>
            <IconButton onClick={() => setInvoicesModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingInvoices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3} sx={{ mt: 2 }}>
              {/* Service Provider Invoice */}
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Service Provider Invoice
                </Typography>
                {providerInvoice ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Invoice Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {(providerInvoice.attributes || providerInvoice).invoiceNumber || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          OMR {((providerInvoice.attributes || providerInvoice).total || 0).toFixed(3)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Items</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddInvoiceItem('customer', providerInvoice.id ?? (providerInvoice.attributes as any)?.id)} sx={{ fontSize: '0.7rem' }}>Add Item</Button>
                      </Box>
                      {providerInvoiceItems.length > 0 ? (
                        <Stack spacing={0.5}>
                          {providerInvoiceItems.map((item: any, idx: number) => {
                            const itemAttrs = item.attributes ?? item;
                            return (
                              <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" display="block">{itemAttrs.description ?? '-'}</Typography>
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600}>OMR {(itemAttrs.total ?? 0).toFixed(3)}</Typography>
                                  <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'customer')} sx={{ p: 0.5 }}><Edit sx={{ fontSize: '0.9rem' }} /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'customer')} sx={{ p: 0.5 }} color="error"><Delete sx={{ fontSize: '0.9rem' }} /></IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No items yet. Click &quot;Add Item&quot; to add one.</Typography>
                      )}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" color="primary" startIcon={<PdfIcon />} onClick={async () => { const invoiceId = providerInvoice.id || providerInvoice.attributes?.id; if (invoiceId) await handleDownloadInvoicePDFById(invoiceId, 'customer'); }}>Download PDF</Button>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">No service provider invoice found</Alert>
                )}
              </Paper>

              {/* Client Invoice */}
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Client Invoice
                </Typography>
                {clientInvoice ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Invoice Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {(clientInvoice.attributes || clientInvoice).invoiceNumber || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          OMR {((clientInvoice.attributes || clientInvoice).total || 0).toFixed(3)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Items</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddInvoiceItem('customer', clientInvoice.id ?? (clientInvoice.attributes as any)?.id)} sx={{ fontSize: '0.7rem' }}>Add Item</Button>
                      </Box>
                      {clientInvoiceItems.length > 0 ? (
                        <Stack spacing={0.5}>
                          {clientInvoiceItems.map((item: any, idx: number) => {
                            const itemAttrs = item.attributes ?? item;
                            return (
                              <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" display="block">{itemAttrs.description ?? '-'}</Typography>
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600}>OMR {(itemAttrs.total ?? 0).toFixed(3)}</Typography>
                                  <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'customer')} sx={{ p: 0.5 }}><Edit sx={{ fontSize: '0.9rem' }} /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'customer')} sx={{ p: 0.5 }} color="error"><Delete sx={{ fontSize: '0.9rem' }} /></IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No items yet. Click &quot;Add Item&quot; to add one.</Typography>
                      )}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" color="primary" startIcon={<PdfIcon />} onClick={async () => { const invoiceId = clientInvoice.id || clientInvoice.attributes?.id; if (invoiceId) await handleDownloadInvoicePDFById(invoiceId, 'customer'); }}>Download PDF</Button>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">No client invoice found</Alert>
                )}
              </Paper>

              {/* Insurance Invoice */}
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Insurance Company Invoice
                </Typography>
                {insuranceInvoice ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Invoice Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {(insuranceInvoice.attributes || insuranceInvoice).invoiceNumber || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          OMR {((insuranceInvoice.attributes || insuranceInvoice).total || 0).toFixed(3)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Items</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddInvoiceItem('insurance', insuranceInvoice.id ?? (insuranceInvoice.attributes as any)?.id)} sx={{ fontSize: '0.7rem' }}>Add Item</Button>
                      </Box>
                      {insuranceInvoiceItemsAllModal.length > 0 ? (
                        <Stack spacing={0.5}>
                          {insuranceInvoiceItemsAllModal.map((item: any, idx: number) => {
                            const itemAttrs = item.attributes ?? item;
                            return (
                              <Box key={item.id ?? idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" display="block">{itemAttrs.description ?? '-'}</Typography>
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>Qty: {itemAttrs.quantity ?? 0} × OMR {(itemAttrs.unitPrice ?? 0).toFixed(3)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600}>OMR {(itemAttrs.total ?? 0).toFixed(3)}</Typography>
                                  <IconButton size="small" onClick={() => handleEditInvoiceItem(item, 'insurance')} sx={{ p: 0.5 }}><Edit sx={{ fontSize: '0.9rem' }} /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteInvoiceItem(item, 'insurance')} sx={{ p: 0.5 }} color="error"><Delete sx={{ fontSize: '0.9rem' }} /></IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No items yet. Click &quot;Add Item&quot; to add one.</Typography>
                      )}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="contained" color="primary" startIcon={<PdfIcon />} onClick={async () => { const invoiceId = insuranceInvoice.id || insuranceInvoice.attributes?.id; if (invoiceId) await handleDownloadInvoicePDFById(invoiceId, 'insurance'); }}>Download PDF</Button>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">No insurance invoice found</Alert>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoicesModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Item Modal */}
      <Dialog
        open={editInvoiceItemModalOpen}
        onClose={() => {
          if (!savingInvoiceItem) {
            setEditInvoiceItemModalOpen(false);
            setEditingInvoiceId(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingInvoiceItem ? 'Edit Invoice Item' : 'Add Invoice Item'}
          {editingInvoiceType && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {editingInvoiceType === 'customer' ? 'Customer Invoice' : 'Insurance Invoice'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              value={editItemFormData.description}
              onChange={(e) => setEditItemFormData({ ...editItemFormData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <NumberTextField
                label="Quantity"
                value={editItemFormData.quantity}
                onChange={(e) => setEditItemFormData({ ...editItemFormData, quantity: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                required
                allowDecimal={false}
              />
              <NumberTextField
                label="Unit Price (OMR)"
                value={editItemFormData.unitPrice}
                onChange={(e) => setEditItemFormData({ ...editItemFormData, unitPrice: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.001 }}
                required
              />
            </Box>
            <NumberTextField
              label="Discount (OMR)"
              value={editItemFormData.discount}
              onChange={(e) => setEditItemFormData({ ...editItemFormData, discount: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, step: 0.001 }}
              fullWidth
            />
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editItemFormData.apply5PercentTax}
                    onChange={(e) => setEditItemFormData({
                      ...editItemFormData,
                      apply5PercentTax: e.target.checked,
                      tax: e.target.checked ? 0 : editItemFormData.tax
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Apply 5% Tax Automatically</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tax will be calculated as 5% of (Subtotal - Discount)
                    </Typography>
                  </Box>
                }
              />
              {!editItemFormData.apply5PercentTax && (
                <NumberTextField
                  label="Tax (OMR)"
                  value={editItemFormData.tax}
                  onChange={(e) => setEditItemFormData({ ...editItemFormData, tax: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.001 }}
                  fullWidth
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Calculation
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                  <Typography variant="body2">OMR {calculateItemTotal().subtotal.toFixed(3)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Discount:</Typography>
                  <Typography variant="body2" color="error.main">- OMR {(editItemFormData.discount ?? 0).toFixed(3)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">After Discount:</Typography>
                  <Typography variant="body2">OMR {calculateItemTotal().afterDiscount.toFixed(3)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Tax {editItemFormData.apply5PercentTax ? '(5%)' : ''}:</Typography>
                  <Typography variant="body2">+ OMR {calculateItemTotal().tax.toFixed(3)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    OMR {calculateItemTotal().total.toFixed(3)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditInvoiceItemModalOpen(false);
              setEditingInvoiceId(null);
            }}
            disabled={savingInvoiceItem}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveInvoiceItem}
            variant="contained"
            disabled={savingInvoiceItem || !editItemFormData.description.trim()}
          >
            {savingInvoiceItem ? 'Saving...' : (editingInvoiceItem ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Request Modal */}
      <Dialog open={createModalOpen} onClose={handleCloseCreateModal} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Request</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Client Search */}
            <Autocomplete
              fullWidth
              options={allCustomers}
              value={null}
              onChange={(_, newValue) => handleClientSelect(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option.data?.attributes || option;
                const clientType = option._type || (attrs.vinNumber ? 'insurance' : 'customer');
                
                if (clientType === 'insurance') {
                  const name = attrs.customerName || '';
                  const vin = attrs.vinNumber || '';
                  return name && vin ? `${name} (${vin})` : name || vin || `Client ${option.id}`;
                } else {
                  const name = attrs.contactPerson || attrs.companyName || '';
                  const customerNum = attrs.customerNumber || '';
                  return name && customerNum ? `${name} (${customerNum})` : name || customerNum || `Customer ${option.id}`;
                }
              }}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter(option => {
                  const attrs = option.attributes || option.data?.attributes || option;
                  const clientType = option._type || (attrs.vinNumber ? 'insurance' : 'customer');
                  
                  if (clientType === 'insurance') {
                    const name = (attrs.customerName || '').toLowerCase();
                    const vin = (attrs.vinNumber || '').toLowerCase();
                    const phone = (attrs.phoneNumber || '').toLowerCase();
                    const search = inputValue.toLowerCase();
                    return name.includes(search) || vin.includes(search) || phone.includes(search);
                  } else {
                    const name = ((attrs.contactPerson || '') + ' ' + (attrs.companyName || '')).toLowerCase();
                    const customerNum = (attrs.customerNumber || '').toLowerCase();
                    const phone = ((attrs.phone || '') + ' ' + (attrs.mobile || '')).toLowerCase();
                    const email = (attrs.email || '').toLowerCase();
                    const search = inputValue.toLowerCase();
                    return name.includes(search) || customerNum.includes(search) || phone.includes(search) || email.includes(search);
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by VIN, Name, or Customer Number *"
                  placeholder="Type VIN, customer name, or customer number..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option.data?.attributes || option;
                const clientType = option._type || (attrs.vinNumber ? 'insurance' : 'customer');
                
                if (clientType === 'insurance') {
                  return (
                    <li {...props} key={option.id}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {attrs.customerName || 'No name'}
                          </Typography>
                          <Chip label="Insurance" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          VIN: {attrs.vinNumber || 'N/A'} | Phone: {attrs.phoneNumber || 'N/A'}
                        </Typography>
                      </Box>
                    </li>
                  );
                } else {
                  return (
                    <li {...props} key={option.id}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {attrs.contactPerson || attrs.companyName || 'No name'}
                          </Typography>
                          <Chip label="Customer" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {attrs.companyName ? `Company: ${attrs.companyName} | ` : ''}
                          Customer #: {attrs.customerNumber || 'N/A'} | 
                          Phone: {attrs.phone || attrs.mobile || 'N/A'}
                        </Typography>
                      </Box>
                    </li>
                  );
                }
              }}
              loading={checkingClient}
              noOptionsText="No customers found"
            />

            {/* Client Info Display */}
            {selectedClient && clientPackageInfo && (
              <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {selectedClient.vinNumber || selectedClient.attributes?.vinNumber 
                      ? `Insurance Client: ${selectedClient.customerName || selectedClient.attributes?.customerName || 'N/A'}`
                      : `Customer: ${selectedClient.contactPerson || selectedClient.attributes?.contactPerson || selectedClient.companyName || selectedClient.attributes?.companyName || 'N/A'}`}
                  </Typography>
                  {selectedClient.vinNumber || selectedClient.attributes?.vinNumber ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        VIN: {selectedClient.vinNumber || selectedClient.attributes?.vinNumber || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Policy: {clientPackageInfo.policy?.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Customer #: {selectedClient.customerNumber || selectedClient.attributes?.customerNumber || 'N/A'}
                      </Typography>
                      {selectedClient.companyName || selectedClient.attributes?.companyName ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Company: {selectedClient.companyName || selectedClient.attributes?.companyName}
                        </Typography>
                      ) : null}
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    Package: {selectedClient.servicePackage?.name || selectedClient.servicePackage?.code || clientPackageInfo.client?.servicePackage?.name || clientPackageInfo.customer?.servicePackage?.name || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Service Selection */}
            {selectedClient && clientPackageInfo && clientPackageInfo.serviceUsage?.services && (
              <FormControl fullWidth required>
                <InputLabel>Service Type *</InputLabel>
                <Select
                  value={createFormData.serviceCode}
                  label="Service Type *"
                  onChange={(e) => {
                    const serviceCode = e.target.value;
                    const service = clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === serviceCode
                    );
                    setCreateFormData({
                      ...createFormData,
                      serviceCode: serviceCode,
                      serviceType: service?.serviceName || ''
                    });
                  }}
                  required
                >
                  <MenuItem value="">
                    <em>Select a service</em>
                  </MenuItem>
                  {clientPackageInfo.serviceUsage?.services
                    ?.filter((service: any) => service.canUseAgain && service.remainingCount > 0)
                    .map((service: any) => (
                      <MenuItem key={service.serviceCode} value={service.serviceCode}>
                        {service.serviceName} ({service.remainingCount} remaining)
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            {/* Phone Number */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Country Code</InputLabel>
                <Select
                  value={countryCode}
                  label="Country Code"
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  <MenuItem value="+968">+968 (Oman)</MenuItem>
                  <MenuItem value="+971">+971 (UAE)</MenuItem>
                  <MenuItem value="+966">+966 (Saudi)</MenuItem>
                  <MenuItem value="+974">+974 (Qatar)</MenuItem>
                  <MenuItem value="+965">+965 (Kuwait)</MenuItem>
                  <MenuItem value="+973">+973 (Bahrain)</MenuItem>
                  <MenuItem value="+1">+1 (US/Canada)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Phone Number *"
                value={createFormData.phoneNumber}
                onChange={(e) => {
                  // Remove any non-digit characters except + at the start
                  const value = e.target.value.replace(/[^\d+]/g, '').replace(/^\+/, '');
                  setCreateFormData({ ...createFormData, phoneNumber: value });
                }}
                required
                placeholder="Enter phone number..."
                InputProps={{
                  startAdornment: <InputAdornment position="start">{countryCode}</InputAdornment>,
                }}
              />
            </Box>

            {/* Location Map */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Location * (Click on map to select)
              </Typography>
              
              {/* Map Picker */}
              <Box sx={{ height: 400, width: '100%', mb: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler
                    onLocationSelect={(lat, lng) => {
                      setLocationLatitude(lat);
                      setLocationLongitude(lng);
                      setMapCenter([lat, lng]);
                    }}
                  />
                  {locationLatitude !== null && locationLongitude !== null && (
                    <Marker position={[locationLatitude, locationLongitude]} />
                  )}
                </MapContainer>
              </Box>

              {/* Display selected coordinates */}
              {locationLatitude !== null && locationLongitude !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Selected: {locationLatitude.toFixed(6)}, {locationLongitude.toFixed(6)}
                </Typography>
              )}

              {/* Button to get current location */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        const lat = position.coords.latitude;
                        const lng = position.coords.longitude;
                        setLocationLatitude(lat);
                        setLocationLongitude(lng);
                        setMapCenter([lat, lng]);
                      },
                      (error) => {
                        console.error('Error getting location:', error);
                        setError('Unable to get your current location');
                      }
                    );
                  } else {
                    setError('Geolocation is not supported by your browser');
                  }
                }}
                sx={{ mt: 1 }}
              >
                Use My Current Location
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal}>Cancel</Button>
          <Button 
            onClick={handleCreateRequest} 
            variant="contained" 
            color="primary"
            disabled={!selectedClient || !createFormData.serviceCode || !createFormData.phoneNumber || !locationLatitude || !locationLongitude}
          >
            Create Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Kanban Dialog */}
      <Dialog
        open={kanbanFullscreen}
        onClose={() => setKanbanFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            m: 0,
            height: '100vh',
            maxHeight: '100vh',
            borderRadius: 0
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="h5">Requests Kanban Board</Typography>
          <IconButton
            onClick={() => setKanbanFullscreen(false)}
            size="small"
            title="Exit Fullscreen"
          >
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)'
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            flex: 1,
            minHeight: 0,
            pb: 2,
            pt: 2,
            px: 2,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
          }}>
            {statusColumns.map((status) => {
              const columnWidth = getColumnWidth(status);
              return (
              <Box key={status} sx={{ 
                flex: `0 0 ${columnWidth}px`, 
                minWidth: `${columnWidth}px`,
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: 2, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: status === 'completed' ? '#e8f5e9' : status === 'cancelled' ? '#ffebee' : 'background.paper',
                    overflow: 'hidden',
                    border: status === 'completed' ? '2px solid #4caf50' : status === 'cancelled' ? '2px solid #f44336' : '1px solid',
                    borderColor: status === 'completed' || status === 'cancelled' ? undefined : 'divider',
                    borderRadius: 2,
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: 8
                    }
                  }}>
                  <Typography variant="h6" gutterBottom sx={{ flexShrink: 0, fontWeight: 600 }}>
                    {getStatusLabel(status)} ({allCategories[status]?.length || 0})
                  </Typography>
                  
                  {/* Show only count for completed and cancelled */}
                  {(status === 'completed' || status === 'cancelled') ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      gap: 2
                    }}>
                      <Typography variant="h2" sx={{ fontWeight: 700, color: status === 'completed' ? 'success.main' : 'error.main' }}>
                        {allCategories[status]?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {status === 'completed' ? 'Completed Requests' : 'Cancelled Requests'}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Show paginated cards for other statuses */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        minHeight: 0,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#ccc',
                          borderRadius: '3px',
                          '&:hover': {
                            background: '#999',
                          },
                        },
                      }}>
                        {paginatedCategories[status]?.map(request => (
                          <RequestCard
                            key={request.id}
                            request={request}
                            onClick={() => {
                              handleOpenDetails(request);
                              setKanbanFullscreen(false);
                            }}
                            getStatusLabel={getStatusLabel}
                            getStatusColor={getStatusColor}
                          />
                        ))}
                      </Box>
                      
                      {/* Pagination controls */}
                      {getTotalPages(status) > 1 && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                          mx: -2,
                          px: 2,
                          pb: 1,
                          borderRadius: '0 0 8px 8px'
                        }}>
                          <Button
                            size="small"
                            onClick={() => handlePageChange(status, (statusPages[status] || 1) - 1)}
                            disabled={(statusPages[status] || 1) <= 1}
                            variant="contained"
                            sx={{ minWidth: 60 }}
                          >
                            ◄
                          </Button>
                          <Chip 
                            label={`${statusPages[status] || 1} / ${getTotalPages(status)}`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                          <Button
                            size="small"
                            onClick={() => handlePageChange(status, (statusPages[status] || 1) + 1)}
                            disabled={(statusPages[status] || 1) >= getTotalPages(status)}
                            variant="contained"
                            sx={{ minWidth: 60 }}
                          >
                            ►
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Map Dialog */}
      <Dialog
        open={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            m: 0,
            height: '100vh',
            maxHeight: '100vh',
            borderRadius: 0
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h5">Service Providers & In-Process Requests Map</Typography>
            <Typography variant="body2" color="text.secondary">
              View active service providers and requests that are in process (not completed or cancelled)
            </Typography>
          </Box>
          <IconButton
            onClick={() => setMapFullscreen(false)}
            size="small"
            title="Exit Fullscreen"
          >
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 120px)',
          position: 'relative'
        }}>
          <Box sx={{ 
            flex: 1, 
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            height: '100%'
          }}>
            <AutomationMap
              providers={serviceProviders}
              requests={requests.filter(r => {
                const status = r.attributes?.status;
                return status !== 'completed' && status !== 'cancelled';
              })}
              onRequestClick={(request) => {
                handleOpenDetails(request);
                setMapFullscreen(false);
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

interface RequestCardProps {
  request: Request;
  onClick: () => void;
  getStatusLabel: (status: string) => string;
  getStatusColor: (status: string) => 'default' | 'primary' | 'success' | 'warning' | 'error';
}

const RequestCard: React.FC<RequestCardProps> = ({ request, onClick, getStatusLabel, getStatusColor }) => {
  const attrs = request.attributes || {};
  const status = attrs.status || 'pending';
  const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
  const notes = attrs.notes || '';
  const createdByAI = attrs.createdByAI || false;
  const isNewUser = attrs.isNewUser || false;
  const isNotOurClient = attrs.isNotOurClient || false;
  const requiresPayment = attrs.requiresPayment || false;
  
  // Check for service restrictions
  const isPolicyExpired = attrs.isPolicyExpired || notes.includes('POLICY EXPIRED') || (attrs.description || '').includes('POLICY EXPIRED');
  const isExtraService = notes.includes('EXTRA SERVICE') || (attrs.description || '').includes('EXTRA SERVICE') || 
                         notes.includes('SERVICE LIMIT REACHED') || (attrs.description || '').includes('SERVICE LIMIT REACHED');
  
  // Determine flag type and message
  let flagType: 'not_our_client' | 'new_user' | 'extra_service' | 'policy_expired' | null = null;
  let flagMessage = '';
  let flagColor = '#f44336';
  
  if (isNotOurClient) {
    flagType = 'not_our_client';
    flagMessage = 'NOT OUR CLIENT - VIN NOT FOUND';
    flagColor = '#d32f2f'; // Dark red
  } else if (isNewUser) {
    flagType = 'new_user';
    flagMessage = 'NEW USER - Not in Database';
    flagColor = '#f44336'; // Red
  } else if (isPolicyExpired) {
    flagType = 'policy_expired';
    flagMessage = 'POLICY EXPIRED';
    flagColor = '#d32f2f'; // Dark red
  } else if (isExtraService || requiresPayment) {
    flagType = 'extra_service';
    flagMessage = 'EXTRA SERVICE - Not in Package';
    flagColor = '#ff5722'; // Deep Orange
  }
  
  // Check for manual assignment tags (only AI couldn't assign, not auto off)
  const needsManualAssignment = notes.includes('need assign manually') && notes.includes('need assign manually (ai couldn\'t)');
  const tagText = needsManualAssignment ? 'AI Couldn\'t Assign' : null;

  return (
    <Card
      sx={{
        cursor: 'pointer',
        '&:hover': { boxShadow: 3 },
        transition: 'box-shadow 0.2s',
        border: flagType ? `3px solid ${flagColor}` : (needsManualAssignment ? '2px solid #ff9800' : 'none'),
        backgroundColor: flagType ? `${flagColor}15` : 'transparent',
        minHeight: { xs: '120px', md: '140px' },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
      onClick={onClick}
    >
      {/* Big Flag Banner */}
      {flagType && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: flagColor,
            color: 'white',
            py: 0.5,
            px: 1,
            zIndex: 1,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {isNotOurClient || isPolicyExpired ? '🔴' : '🚩'} {flagMessage}
          </Typography>
        </Box>
      )}
      
      <CardContent sx={{ 
        p: { xs: 1, md: 1.5 }, 
        '&:last-child': { pb: { xs: 1, md: 1.5 } },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        pt: flagType ? { xs: 3, md: 3.5 } : { xs: 1, md: 1.5 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}>
            {attrs.requestNumber || `#${request.id}`}
          </Typography>
          {createdByAI && (
            <Chip
              label="AI"
              size="small"
              color="info"
              sx={{ 
                fontSize: { xs: '0.65rem', md: '0.7rem' },
                height: { xs: 18, md: 20 },
                fontWeight: 'bold',
                ml: 0.5
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
          {provider?.name || 'No Provider'}
        </Typography>
        {attrs.createdAt && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, fontSize: { xs: '0.65rem', md: '0.7rem' }, opacity: 0.7 }}>
            {new Date(attrs.createdAt).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
          <Chip
            label={getStatusLabel(status)}
            size="small"
            color={getStatusColor(status)}
            sx={{ 
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              height: { xs: 20, md: 24 }
            }}
          />
          {needsManualAssignment && tagText && (
            <Chip
              label={tagText}
              size="small"
              color="warning"
              sx={{ 
                fontSize: { xs: '0.65rem', md: '0.7rem' },
                height: { xs: 18, md: 20 },
                fontWeight: 'bold'
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AutomationDashboard;
